import type {
  Breadcrumb,
  ErrorEvent,
  Event,
  EventHint,
  Exception,
  Integration,
  Log,
  Mechanism,
  Options,
  SpanJSON,
  StackFrame,
  Thread,
  TransactionEvent,
} from "@sentry/core";
import { withIsolationScope } from "@sentry/core";

const FORBIDDEN_KEY_TOKENS = new Set([
  "address",
  "age",
  "authorization",
  "birth",
  "birthdate",
  "body",
  "clerk",
  "completion",
  "contact",
  "cookie",
  "cookies",
  "data",
  "dob",
  "email",
  "field",
  "firstname",
  "form",
  "free",
  "headers",
  "healthie",
  "hubspot",
  "insurance",
  "lastname",
  "line1",
  "line2",
  "member",
  "message",
  "name",
  "note",
  "notes",
  "patient",
  "payload",
  "phone",
  "policy",
  "postal",
  "prompt",
  "raw",
  "session",
  "street",
  "text",
  "token",
  "transcript",
  "zip",
]);
const METHOD_RE = /^(?:DELETE|GET|HEAD|OPTIONS|PATCH|POST|PUT|UNKNOWN)$/;
const STATUS_RE = /^[1-5][0-9]{2}$/;
const BOOLEAN_STRINGS = new Set(["false", "true"]);
const ROUTE_TARGETS = new Set(["always-ai", "ndd-booking"]);
const SAFE_CHECKS = new Set(["db"]);
const SAFE_ERROR_DUMP_STATUSES = new Set(["timed_out", "write_failed"]);
const SAFE_APPOINTMENT_AVAILABILITY_REASONS = new Set([
  "configuration_unavailable",
  "patient_identity_unavailable",
  "provider_unavailable",
]);
const SAFE_APPOINTMENT_TIMEZONE_FALLBACK_REASONS = new Set([
  "invalid_device_timezone",
  "missing_device_timezone",
]);
const SAFE_CODES = new Set([
  "conflict",
  // NO-927: Healthie refused a message ingest read a retry cannot change.
  "healthie_rejected",
  "database_error",
  // NO-775: a manual-generation step that failed on S3 after the save.
  "storage_error",
  "db_health_check_failed",
  "graphql_error",
  "http_error",
  "internal_error",
  "invalid_config",
  "missing_credentials",
  "network_error",
  "not_implemented",
  "rate_limited",
  "shape_rejected",
  "state_error",
  "timeout",
  "unexpected_response",
  "unexpected_error",
  "upstream_validation",
  // NO-784: the Patient Quick Note Healthie import's storage failures.
  "import_storage_failed",
  "import_head_moved",
  // NO-846: a Client Red Flag read or Mark called could not use an Analysis.
  "analysis_missing",
  "analysis_integrity_failed",
  "analysis_read_failed",
  // NO-868: the RD Schedule week's contract-hours store could not be read.
  "contract_unavailable",
]);
// Exact sentinel messages application code constructs with `new Error(...)`
// for Sentry captures. Free-form error text never matches; anything outside
// this reviewed list stays redacted.
const SAFE_ERROR_MESSAGES = new Set([
  "analysis_cleanup_failed",
  "analysis_revision_artifact_integrity_failed",
  "analysis_status_update_failed",
  "appointment_note_autosave_failed",
  "appointment_note_read_failed",
  "appointment_note_save_failed",
  // NO-445 Patient Quick Note route and autosave failure sentinels.
  "patient_quick_note_autosave_failed",
  "patient_quick_note_read_failed",
  "patient_quick_note_save_failed",
  // NO-750 Patient Quick Note Healthie sync route and retry failure sentinels.
  "patient_quick_note_healthie_sync_failed",
  "patient_quick_note_healthie_sync_retry_failed",
  "patient_quick_note_projection_failed",
  // NO-870 RD hours Healthie mirror failure sentinel (no cause attached).
  "rd_hours_mirror_failed",
  // NO-846 Client Red Flag route failure sentinels.
  "patient_red_flags_read_failed",
  "patient_red_flags_mark_failed",
  // NO-847 patient page read failure sentinel.
  "patient_page_read_failed",
  // NO-925 Messages read failure sentinel.
  "messages_read_failed",
  // NO-868 RD Schedule week read failure sentinel.
  "rd_schedule_read_failed",
  // NO-876: an RD Schedule appointment dialog request that failed in the browser.
  "rd_schedule_action_client_failed",
  // NO-882 failed Clinical Read Event write: AlwaysAI reports this sentinel
  // instead of the database error, whose text can carry row values.
  "clinical_read_write_failed",
  // NO-852 Quick facts and Goals route failure sentinels.
  "patient_quick_facts_and_goals_read_failed",
  "patient_quick_facts_and_goals_save_failed",
  // NO-867 RD hours route failure sentinels.
  "rd_hours_read_failed",
  "rd_hours_save_failed",
  // NO-871 RD blocked time route failure sentinels.
  "rd_blocks_read_failed",
  "rd_blocks_save_failed",
  "appointment_previous_soap_notes_failed",
  "appointment_previous_soap_notes_list_network_failed",
  "appointment_previous_soap_note_selection_network_failed",
  "appointment_presence_heartbeat_failed",
  "appointment_documentation_snapshot_failed",
  "appointment_documentation_enhance_failed",
  // NO-679 delivered-note Healthie click-through failure sentinel.
  "appointment_documentation_healthie_note_failed",
  // NO-772 Enhance progress stream failure sentinel.
  "appointment_documentation_stream_failed",
  // NO-775 manual generation from a pasted Transcript: the route and client
  // failure sentinel, and a step that failed after the Transcript was saved.
  "appointment_documentation_manual_generation_failed",
  "manual_soap_generation_step_failed",
  // NO-785 RD Re-enhance route and client request failure sentinel.
  "appointment_documentation_re_enhance_failed",
  "appointment_documentation_poll_failed",
  "appointment_draft_read_failed",
  "appointment_draft_save_failed",
  "soap_draft_approval_failed",
  "appointment_draft_autosave_failed",
  "appointment_draft_copy_failed",
  "rd_schedule_details_link_copy_failed",
  "call_review_analysis_failed",
  "call_review_status_update_failed",
  "healthie_appointment_parity_failed",
  "healthie_appointment_sync_failed",
  "healthie_prior_soap_notes_response_refused",
  "healthie_prior_soap_notes_configuration_failed",
  "healthie_reference_scheduled_sync_failed",
  // NO-833 shadow report: Healthie's sent emails could not be read.
  "notifications_shadow_report_healthie_failed",
  "ndd_booking_sentry_smoke_server",
  "provider_smoke_operation_failed",
  "provider_smoke_preflight_failed",
  "session_compensation_failed",
  "soap_enhancement_input_unavailable",
  "soap_enhancement_output_discarded",
  "soap_enhancement_reaper_failed",
  "soap_writeback_enqueue_failed",
  "soap_writeback_human_resolution_required",
  "soap_writeback_reaper_failed",
  "soap_writeback_reconciliation_failed",
  // NO-765: a stuck Intent whose chart stays unreadable, raised hourly.
  "soap_writeback_reconciliation_chart_unreadable",
  "soap_writeback_retries_exhausted",
  "soap_enhancement_storage_failed",
  "transcript_source_sweep_failed",
  "soap_generation_failed",
  // NO-591 Session Recap generation failure sentinel.
  "session_recap_generation_failed",
  "prior_soap_note_artifact_capture_failed",
  "worker_health_check_in_failed",
  // AlwaysAI SQS worker: `always_ai_worker_${operation}_failed` over the
  // @ndd/aws SqsWorkerErrorEvent operation union.
  "always_ai_worker_acknowledge_failed",
  "always_ai_worker_handler_failed",
  "always_ai_worker_heartbeat_failed",
  "always_ai_worker_receive_failed",
  "always_ai_worker_retry_failed",
  // Evaluation worker: `eval_worker_${operation}_failed` over its emitError
  // call sites plus the forwarded SQS operation union.
  "eval_worker_acknowledge_failed",
  "eval_worker_admission_pause_failed",
  "eval_worker_database_idle_failed",
  "eval_worker_handler_failed",
  "eval_worker_heartbeat_failed",
  "eval_worker_load_poll_failed",
  "eval_worker_maintenance_failed",
  "eval_worker_receive_failed",
  "eval_worker_retry_failed",
]);
const SAFE_FAILURE_CODES = new Set([
  "analysis_invalid_request",
  "analysis_provider_unavailable",
  "analysis_rate_limited",
  "analysis_timeout",
  "hubspot_unavailable",
  "transcript_empty",
  "transcript_incomplete",
  "transcript_not_ready",
  "transcript_permission_denied",
  "transcript_too_large",
]);
const SAFE_FLOWS = new Set(["call-review", "session-type-detection", "v2-session"]);
const SAFE_JOB_SLUGS = new Set([
  "analyze-session",
  "detect-session-type",
  "healthie-appointment-media",
  "soap-enhancement",
  "soap-writeback",
  "patient-quick-note-projection",
  // NO-870.
  "rd-hours-healthie-mirror",
  "webhook-health",
]);
const SAFE_ACTIONS = new Set([
  "session_create_queue_state_update",
  "session_create_transcript_cleanup",
  "session_rerun_queue_state_update",
  "session_retry_queue_state_update",
]);
const SAFE_ARTIFACTS = new Set(["analysis", "thinking"]);
const SAFE_ANALYSIS_FAILURE_CATEGORIES = new Set([
  "access_denied",
  "activation_expired",
  "activation_missing",
  "activation_pending",
  "activation_revoked",
  "auth",
  "classification_not_activated",
  "configuration_changed",
  "content_too_large",
  "credentials_expired",
  "model_timeout",
  "network",
  "persistence_completion_write",
  "persistence_final_write",
  "persistence_scratch_write",
  "rate_limit",
  "service_unavailable",
  "throttling",
  "timeout",
  "trace_contract_violation",
  "trace_store_unavailable",
  "unknown",
  "validation",
]);
/**
 * Route families named by `auth.denied` (`patients`: NO-844's Patients list;
 * `rd_hours`: NO-867's RD hours routes; `rd_schedule`: NO-868's RD Schedule
 * week; `rd_schedule_actions`: NO-872's RD Schedule actions).
 */
const SAFE_DENIED_ROUTES = new Set([
  "appointments",
  "patients",
  "rd_hours",
  "rd_schedule",
  "rd_schedule_actions",
]);

/**
 * Which resolution produced an Appointment read's scope (NO-467;
 * `supervised`: NO-868's supervisor selection on the RD Schedule week).
 */
const SAFE_APPOINTMENT_SCOPE_LANES = new Set(["own", "admin", "supervised"]);

/** Which lane reached an RD's hours (NO-867). */
const SAFE_RD_HOURS_LANES = new Set(["own", "supervised", "all"]);

/** Why an Appointment read's RD selection was refused (NO-467). */
const SAFE_APPOINTMENT_SELECTION_DENIALS = new Set([
  "rd_selection_forbidden",
  "rd_selection_malformed",
  "no_appointment_read_scope",
]);

/** Why an RD hours request was refused (NO-867); the selection reasons above also apply. */
const SAFE_RD_HOURS_DENIALS = new Set(["no_rd_hours_scope", "rd_not_supervised"]);

const SAFE_OPERATIONS = new Set([
  "acknowledge",
  "approve",
  "admission_pause",
  "database_idle",
  "detail",
  "fetch_source",
  "handler",
  // NO-844: the Patients list's scope resolution (admin selection, unavailable).
  "patient_list",
  "health_check_in",
  "transcript_source_sweep",
  "heartbeat",
  "list_completed_sources",
  "load_poll",
  "list",
  "maintenance",
  "receive",
  "read",
  "read_streaming",
  "preserve_streaming",
  "delete_scratch",
  "revision_artifact_verify",
  "revision_artifact_write",
  "session_create_queue_state_update",
  "session_create_transcript_cleanup",
  "session_rerun_queue_state_update",
  "session_retry_queue_state_update",
  "retry",
  "save",
  "sync_read",
  "sync_retry",
  "snapshot",
  "enhance",
  // NO-679 delivered-note Healthie click-through capture site.
  "healthie_note",
  // NO-775 manual generation route, client request, and post-save steps.
  "manual_generation",
  // NO-785 RD Re-enhance route and client request.
  "re_enhance",
  // NO-846 Client Red Flag scope resolution and Mark called capture site.
  "red_flags",
  "mark_called",
  "legacy_alias",
  "claim",
  "stream",
  "status_update",
  // NO-472 appointment status reconciliation capture sites: the adapter's
  // Healthie read/update phases and the ingestion workflow's outer catch.
  "update",
  "reconcile",
  // NO-476 prior SOAP Note read capture sites, one per port operation.
  "browse_recent_signed_notes",
  "fetch_recent_signed_note",
  "fetch_latest_qualifying_note",
  "configure",
  "copy",
  "write",
  "verify_digest",
  "read_transcript_artifact",
  "read_prior_note_artifact",
  // NO-480 SOAP writeback capture sites: the self-consistency guard that runs
  // before any vendor call, the pre-write read that detects a collision, the
  // note create, and the post-write verification re-read.
  "writeback_guard",
  "writeback_pre_read",
  "writeback_create",
  // NO-542 reconciliation's two read operations against the chart.
  "reconciliation_pre_read",
  "reconciliation_verify",
  "writeback_verify",
  // NO-467 admin-scoped Appointment Note read.
  "note",
  // NO-482 trusted provider smoke preflight and provider phases. The
  // `bedrock_generate` and `openai_generate` values are shared with the
  // NO-492 SOAP generation failure sentinel (`soap_generation_failed`).
  "command",
  "declaration",
  "environment",
  "aws_identity",
  "bedrock_generate",
  "openai_generate",
  "soap_writeback",
  // NO-591 Session Recap: the generation sentinel's operation, the progress
  // channel's publish (read is listed above), and the owning-RD flag lookup.
  "session_recap_generate",
  "publish",
  "owner_lookup",
  // NO-847: the patient page's Appointment scope resolution and read failure.
  "patient_page",
  // NO-872: the RD Schedule action Healthie capture sites, one per call.
  "create",
  "delete",
  "update_status",
  "list_window",
  "read_appointment",
  "read_private_note",
  // NO-868: the RD Schedule week's scope resolution and read failure.
  "rd_schedule",
]);
const SAFE_ANALYSIS_REVISION_PHASES = new Set([
  "completion",
  "failure",
  "heartbeat",
  "release",
]);
const SAFE_ANALYSIS_REVISION_CREATION_MODES = new Set(["legacy", "revision"]);
// NO-473 enhancement_persistence.* refusal reasons: a closed vocabulary the
// domain module emits with no identifiers alongside it. The snapshot_rejected
// members name which authoritative cross-check refused the caller's facts.
const SAFE_ENHANCEMENT_PERSISTENCE_REASONS = new Set([
  "already_terminal",
  "appointment_identity_incomplete",
  "appointment_note_appointment_mismatch",
  "appointment_note_digest_mismatch",
  "appointment_note_revision_not_found",
  "appointment_metadata_mismatch",
  "appointment_session_mismatch",
  "clinical_session_identity_mismatch",
  "appointmentless_session",
  "configuration_not_found",
  // NO-479: a generated Version whose content the editor cannot mount, so the
  // Draft surface stays read-only instead of coercing content.
  "document_unsupported",
  "healthie_appointment_mismatch",
  "healthie_patient_mismatch",
  "healthie_patient_unresolved",
  "no_draft",
  "no_generation_provenance",
  "not_running",
  "prompt_digest_mismatch",
  "prompt_revision_mismatch",
  "routing_policy_mismatch",
  "session_not_found",
  // NO-774: a success refused because the worker never froze the snapshot.
  "snapshot_pending",
  // NO-785: a Re-enhance whose Draft head moved or was approved is cancelled,
  // and edits and approval wait while a Re-enhance runs.
  "draft_superseded",
  "re_enhancing",
  "template_digest_mismatch",
  "template_revision_mismatch",
  "transcript_artifact_key_mismatch",
  "transcript_digest_mismatch",
  "transcript_version_not_found",
  "transcript_version_session_mismatch",
]);
const SAFE_PRIOR_SOAP_NOTE_INCOMPLETE_REASONS = new Set([
  "current_appointment_not_found",
  "current_appointment_patient_mismatch",
  "current_appointment_start_missing",
  "current_appointment_start_mismatch",
  "invalid_service_timestamp",
  "invalid_signature_timestamp",
  "invalid_current_appointment_timestamp",
  "missing_answer_module_identity",
  "missing_page_cursor",
  "multiple_soap_module_answers",
  "provider_response_incomplete",
  "scan_exhausted",
  "unreadable_appointment_start",
]);
const SAFE_PRIOR_SOAP_NOTE_RESPONSE_REFUSAL_REASONS = new Set([
  "form_mismatch",
  "missing_answer_module_identity",
  "multiple_soap_module_answers",
  "patient_mismatch",
]);
// NO-675: log-only reasons for answers a rendered prior Note left out; no
// exception carries them, so they stay out of acceptsAnySafeReason.
const SAFE_PRIOR_SOAP_NOTE_OMISSION_REASONS = new Set([
  "empty_answer",
  "non_text_answer",
]);
const SAFE_PRIOR_SOAP_NOTE_REASONS = new Set([
  ...SAFE_PRIOR_SOAP_NOTE_INCOMPLETE_REASONS,
  ...SAFE_PRIOR_SOAP_NOTE_RESPONSE_REFUSAL_REASONS,
]);
// NO-475 durable Enhancement claim and worker job: closed refusal reasons the
// claim adapter, the Appointment-to-Session consumer, and the job emit. Job
// events may also carry the opaque generation id; none carry clinical content.
const SAFE_ENHANCEMENT_CLAIM_REFUSAL_REASONS = new Set([
  "appointment_identity_ambiguous",
  "appointment_identity_incomplete",
  "authorization_denied",
  "claim_key_exhausted",
  "configuration_missing",
  "prior_note_configuration_missing",
  "prior_note_digest_mismatch",
  "prior_note_incomplete",
  "prior_note_storage_unavailable",
  "prior_note_unavailable",
  "session_missing",
  "session_mismatch",
  "snapshot_rejected",
  "transcript_missing",
]);
// NO-785: why an RD Re-enhance was refused. Closed codes only.
const SAFE_RE_ENHANCEMENT_REFUSAL_REASONS = new Set([
  "authorization_denied",
  "draft_approved",
  "draft_changed",
  "no_draft",
  "session_mismatch",
]);
const SAFE_ENHANCEMENT_SESSION_UNRESOLVED_REASONS = new Set([
  "ambiguous_session",
  "no_session",
]);
const SAFE_APPOINTMENT_SESSION_MALFORMED_REASONS = new Set([
  "appointment_mismatch",
  "provider_mismatch",
  "patient_mismatch",
  "provider_and_patient_mismatch",
  "session_kind_mismatch",
]);
const SAFE_SOAP_ENHANCEMENT_IGNORED_REASONS = new Set([
  "attempt_not_found",
  "attempt_not_running",
  "enhancement_mismatch",
  "enhancement_succeeded",
  "generation_cancelled",
]);
const SAFE_SOAP_ENHANCEMENT_INPUT_FAILURES = new Set([
  "artifact_missing",
  "model_configuration_unsupported",
  "prior_note_artifact_missing",
  "prior_note_digest_mismatch",
  "prior_note_provenance_incomplete",
  "transcript_digest_mismatch",
]);
const SAFE_SOAP_ENHANCEMENT_DISCARD_REASONS = new Set([
  "attempt_not_found",
  "draft_superseded",
]);
const SAFE_SOAP_STORAGE_REASONS = new Set(["storage_denied", "storage_unavailable"]);
// NO-556 published supervisor metrics storage lane. `reason` rides the
// per-event map below and the tag acceptor; it names why a read or publish
// returned `unavailable`, never what the stored payload held. NO-618 retired
// `partial_batch`: a publication that omits a requested RD is a per-RD display
// state, not a storage failure, so nothing emits that reason any more.
// NO-557 Metrics Import Run. Every one of these is a closed code the importer
// owns: a stage failure, an `apply` refusal, a source or value rejection, an
// identity skip, and the one reason a read-only Postgres port returns. Row
// indices, display names and values never ride a log line — they go to the
// Import Reconciliation Report — so nothing here can carry an RD identity.
const SAFE_METRICS_IMPORT_FAILURE_REASONS = new Set([
  "source_unreadable",
  "source_rejected",
  "directory_unavailable",
  "manifest_rejected",
  "cutoff_splits_observed_week",
  "report_refused",
  "publication_unavailable",
  "unexpected_error",
  "query_failed",
]);
const SAFE_METRICS_IMPORT_REFUSAL_REASONS = new Set([
  "source_digest_mismatch",
  "plan_digest_mismatch",
  "unsupported_source_format",
  "publication_ahead_of_plan",
]);
const SAFE_METRICS_IMPORT_IDENTITY_SKIP_REASONS = new Set([
  "unmatched_name",
  "ambiguous_name",
  "duplicate_source_row",
  "alias_target_unknown",
]);
// A `code` is either a structural rejection the parsers raise or the reason a
// source could not be read at all; `metrics-import-log-scrubbing.test.ts`
// reads both tuples out of the importer and fails when one of them names a
// code this set does not.
const SAFE_METRICS_IMPORT_REJECTION_CODES = new Set([
  "malformed_json",
  "wrong_type",
  "missing_field",
  "unknown_field",
  "duplicate_key",
  "unknown_reference",
  "wrong_vector_length",
  "wrong_order",
  "invalid_date",
  "out_of_range",
  "not_found",
  "outside_input_directory",
  "unreadable",
  "too_large",
  "timed_out",
  "unexpected_error",
]);
const SAFE_METRICS_IMPORT_STAGES = new Set(["inspect", "preflight", "apply", "verify"]);
const SAFE_METRICS_IMPORT_PORTS = new Set([
  "provider_directory",
  "publication_inspection",
]);
const SAFE_METRICS_IMPORT_PUBLICATION_OUTCOMES = new Set([
  "published",
  "reused",
  "conflict",
  "invalid",
  "unavailable",
]);
const SAFE_METRICS_IMPORT_SINKS = new Set(["filesystem", "s3"]);
/** The plan digest an operator passes back as `--expected-plan-sha256`. */
const SHA256_RE = /^[0-9a-f]{64}$/;
const SQLSTATE_RE = /^[0-9A-Z]{5}$/;
const SAFE_QUICK_NOTE_IMPORT_DETECTORS = new Set(["webhook", "projection"]);
const PATIENT_QUICK_NOTE_CONSTRAINT_RE = /^patient_quick_note[a-z0-9_]{0,45}$/;
const SAFE_SUPERVISOR_METRICS_STORAGE_REASONS = new Set([
  "query_failed",
  "payload_rejected",
  "identity_mismatch",
  "fake_provenance",
  "version_mismatch",
  "unexpected_error",
]);
// NO-496 Transcript Source Sweep and immediate cleanup. `reason`
// rides the per-event map below; `runtime` and `outcome` are dispatched by key.
const SAFE_TRANSCRIPT_SOURCE_SWEEP_REASONS = new Set([
  "list_failed",
  "database_error",
  ...SAFE_SOAP_STORAGE_REASONS,
]);
// NO-428: `DraftReadyFailureReason` (the "Draft ready" read and seen write)
// in always-ai.
const SAFE_DRAFT_READY_FAILURE_REASONS = new Set(["query_failed"]);
const SAFE_TRANSCRIPT_SOURCE_CLEANUP_REASONS = new Set([
  "database_error",
  ...SAFE_SOAP_STORAGE_REASONS,
]);
const SAFE_TRANSCRIPT_SOURCE_CLEANUP_OUTCOMES = new Set([
  "deleted",
  "referenced",
  "deferred",
  "failed",
]);
// The process a log line came from: the web or worker task, or an operator's
// one-shot script. Worker alarms key on it.
const SAFE_RUNTIMES = new Set(["web", "worker", "operator"]);
const SAFE_PRIOR_SOAP_ARTIFACT_REASONS = new Set([
  "digest_mismatch",
  "storage_denied",
  "storage_unavailable",
]);
const SAFE_ENHANCEMENT_REASONS = new Set([
  ...SAFE_ENHANCEMENT_CLAIM_REFUSAL_REASONS,
  ...SAFE_RE_ENHANCEMENT_REFUSAL_REASONS,
  ...SAFE_ENHANCEMENT_SESSION_UNRESOLVED_REASONS,
  ...SAFE_APPOINTMENT_SESSION_MALFORMED_REASONS,
  ...SAFE_SOAP_ENHANCEMENT_IGNORED_REASONS,
  ...SAFE_SOAP_ENHANCEMENT_INPUT_FAILURES,
  ...SAFE_SOAP_ENHANCEMENT_DISCARD_REASONS,
  ...SAFE_SOAP_STORAGE_REASONS,
  ...SAFE_PRIOR_SOAP_ARTIFACT_REASONS,
]);
const SAFE_PROVIDER_SMOKE_REASONS = new Set([
  "command_arguments_invalid",
  "declaration_file_unreadable",
  "command_failed",
  "declaration_invalid",
  "production_forbidden",
  "healthie_endpoint_not_staging",
  "healthie_credentials_missing",
  "openai_credentials_missing",
  "credential_lane_invalid",
  "writeback_environment_invalid",
  "writeback_target_unapproved",
  "dependency_configuration_invalid",
  "aws_identity_unavailable",
  "aws_account_unapproved",
  "aws_role_unapproved",
  "healthie_provider_unavailable",
  "healthie_provider_rejected",
  "transcript_source_unavailable",
  "transcript_source_incomplete",
  "transcript_source_expired",
  "declared_transcript_missing",
  "transcript_identity_mismatch",
  "transcript_digest_mismatch",
  "transcript_fixture_mismatch",
  "prior_note_missing",
  "healthie_response_incomplete",
  "prior_note_digest_mismatch",
  "prior_note_fixture_mismatch",
  "model_unavailable",
  "model_rejected",
  "model_output_invalid",
  "model_identity_mismatch",
  "model_output_digest_mismatch",
  "writeback_collision",
  "writeback_verification_failed",
  "writeback_note_locked",
  "writeback_note_signed",
  "writeback_indeterminate",
  "writeback_chart_ambiguous",
  "writeback_request_invalid",
  "writeback_target_mismatch",
  "operation_failed",
]);
const SAFE_SOAP_WRITEBACK_REASONS = new Set([
  "verification_read_failed",
  "note_missing",
  "note_id_mismatch",
  "appointment_mismatch",
  "patient_mismatch",
  "form_mismatch",
  "module_missing",
  "module_ambiguous",
  "module_mismatch",
  "external_id_mismatch",
  "note_locked",
  "note_signed",
  "multiple_intent_notes",
  "missing_form_identity",
  "missing_module_identity",
  "multiple_mapped_soap_answers",
  "payload_digest_mismatch",
  "non_canonical_identifier",
  "execution_guard_unavailable",
  "execution_guard_outcome_unknown",
  "appointment_not_found",
  "appointment_start_invalid",
  "appointment_patient_mismatch",
  "provider_unavailable",
  "provider_rejected",
  "create_outcome_unknown",
  // NO-542 settlement reasons, skip reasons, and human-resolution refusals.
  "note_verified",
  "note_absent",
  "grace_pending",
  "chart_unavailable",
  "already_settled",
  "intent_not_stuck",
  "note_id_required",
]);
// NO-542 settlement outcomes: what automation or an operator concluded about
// one stuck writeback attempt.
const SAFE_SOAP_WRITEBACK_SETTLEMENT_OUTCOMES = new Set([
  "delivered",
  "retry_safe",
  "needs_human",
  "closed",
]);
// NO-765 durable writeback loop: why an Intent was not frozen, why a delivery
// was ignored, and how an attempt settled.
const SAFE_SOAP_WRITEBACK_LOOP_REASONS = new Set([
  "writeback_not_configured",
  "approval_missing",
  "writeback_target_incomplete",
  "approved_soap_text_empty",
  "non_canonical_writeback_identity",
  "attempt_not_found",
  "attempt_not_running",
  "intent_mismatch",
  "settlement_lost",
]);
// NO-783: why an RD-initiated Healthie write used the service key instead of
// the RD's own, which key it used, and why a stored RD key could not be read.
const SAFE_HEALTHIE_WRITE_FALLBACK_REASONS = new Set([
  "no_rd_actor",
  "rd_key_missing",
  "rd_key_unreadable",
  "rd_key_rejected",
  "rd_key_store_unconfigured",
]);
const SAFE_HEALTHIE_WRITE_CREDENTIALS = new Set(["rd_key", "service_key"]);
const SAFE_HEALTHIE_KEY_STORE_CODES = new Set([
  "invalid_input",
  "active_key_exists",
  "healthie_api_key_id_taken",
  "encrypt_failed",
  "decrypt_failed",
  "database_failed",
  "unexpected_error",
]);
const SAFE_SOAP_WRITEBACK_ATTEMPT_STATUSES = new Set([
  "delivered",
  "collision",
  "failed",
  "cancelled",
]);
// NO-765 why a vendor refused a call, as closed facts (`healthieFailureFacts`
// in @ndd/healthie, plus the writeback's `create_conflict`). The vendor's own
// message text never reaches these keys.
const SAFE_VENDOR_CODES = new Set([
  "missing_credentials",
  "invalid_config",
  "invalid_input",
  "timeout",
  "network_error",
  "http_error",
  "graphql_error",
  "unexpected_response",
  "upstream_validation",
  "conflict",
  "rate_limited",
  "not_found",
]);
const SAFE_VENDOR_REASONS = new Set([
  "duplicate_email",
  "invalid_email",
  "invalid_phone",
  "missing_required_field",
  "provider_not_allowed",
  "patient_signup_not_allowed",
  "form_not_found",
  "module_not_on_form",
  "required_answer_missing",
  "permission_denied",
  "recording_not_found",
  "unknown_validation",
  "create_conflict",
]);
const SAFE_VENDOR_FIELDS = new Set([
  "answer",
  "appointment_id",
  "base",
  "custom_module_form_id",
  "custom_module_id",
  "datetime",
  "dietitian_id",
  "email",
  "first_name",
  "form_answer_group",
  "form_answers",
  "last_name",
  "phone",
  "phone_number",
  "provider",
  "provider_id",
  "timezone",
  "user_id",
  // NO-832: the notification-setting fields the RD alerts switch reads and
  // writes, which a refused update may name (field names, never values).
  "id",
  "send_email_before_appointment",
  "send_push_before_appointment",
  "send_email_on_appointment_book",
  "send_email_on_appointment_cancel",
  "send_email_on_appointment_reschedule",
  "send_email_on_appt_request_for_anyone",
  "send_email_on_appt_request_for_you",
]);
const VENDOR_OPERATION_RE = /^[a-z][A-Za-z0-9_]{0,63}$/;

/**
 * The vendor-fact vocabularies the scrubber accepts, exported read-only so the
 * apps that produce the facts can prove they agree (NO-765). A fact outside
 * them is redacted, so drift loses diagnostics but never leaks.
 */
export const SENTRY_VENDOR_FACT_VOCABULARIES: {
  readonly codes: ReadonlySet<string>;
  readonly reasons: ReadonlySet<string>;
  readonly fields: ReadonlySet<string>;
} = {
  codes: SAFE_VENDOR_CODES,
  reasons: SAFE_VENDOR_REASONS,
  fields: SAFE_VENDOR_FIELDS,
};
// Query parameter NAMES from an outgoing request's URL; the values never leave
// the process.
const QUERY_KEY_RE = /^[A-Za-z][A-Za-z0-9_.-]{0,39}$/;
const MAX_LISTED_NAMES = 20;
// NO-440 recording import and Healthie copy deletion: why a stored recording
// failed verification, and how an archival run ended.
const SAFE_RECORDING_IMPORT_REASONS = new Set([
  "stored_object_missing",
  "byte_count_mismatch",
  "digest_mismatch",
  "source_size_mismatch",
]);
// The one reason a delivery with nothing to archive reports.
const SAFE_RECORDING_ARCHIVE_SKIP_REASONS = new Set(["already_archived"]);
const SAFE_RECORDING_IMPORT_OUTCOMES = new Set([
  "healthie_copy_deleted",
  "healthie_copy_already_absent",
]);
// NO-745: what the media job did when a Transcript Version covering more
// Healthie transcripts superseded the one the Session's Analysis read.
const SAFE_TRANSCRIPT_SUPERSEDED_OUTCOMES = new Set([
  "analysis_requeued",
  "analysis_kept_after_approval",
  "analysis_in_flight",
]);
// NO-775: why a manual generation from a pasted Transcript was refused, and
// how one that reached the claim ended. Closed codes only.
const SAFE_MANUAL_SOAP_GENERATION_REFUSAL_REASONS = new Set([
  "transcript_exists",
  "appointment_identity_incomplete",
  "appointment_identity_ambiguous",
  "appointment_identity_unresolved",
  "session_conflict",
  "generation_not_started",
]);
const SAFE_MANUAL_SOAP_GENERATION_OUTCOMES = new Set([
  "claimed",
  "already_active",
  "already_complete",
  "replayed",
]);
// NO-784: what one look at a Healthie edit inside the Patient Quick Note
// block decided. Closed codes only; the Healthie text never rides along.
const SAFE_PATIENT_QUICK_NOTE_IMPORT_OUTCOMES = new Set([
  "disabled",
  "conflict",
  "no_block",
  "no_baseline",
  "echo",
  "already_imported",
  "imported",
  "unlinked",
  "stale_read",
]);
// NO-927: what the Healthie message ingest did with one message, and why it
// skipped one. Closed vocabularies: no person, message or text rides.
const SAFE_MESSAGE_INGEST_OUTCOMES = new Set([
  "stored",
  "deleted",
  "duplicate",
  "skipped",
  "deferred",
]);
const SAFE_MESSAGE_INGEST_SKIP_REASONS = new Set([
  "note_not_found",
  "chat_not_found",
  "group_chat",
  "members_unmatched",
  "unknown_sender",
  "flag_off",
  "flag_unreadable",
  "deleted_unknown",
  "attachment_only",
  "empty_message",
]);
// NO-763: what the media job decided about one downloaded Healthie transcript.
const SAFE_TRANSCRIPT_SOURCE_CHECK_OUTCOMES = new Set([
  "accepted",
  "grew",
  "unchanged",
  "not_ready",
  "unavailable",
  "refused",
]);
// NO-786: whether a downloaded Healthie transcript gave the session's actual
// start and end, or the closed reason it did not. Never a time or offset.
const SAFE_TRANSCRIPT_TIMING_OUTCOMES = new Set([
  "computed",
  "part_start_missing",
  "part_start_unparseable",
  "cue_unparseable",
  "no_cues",
  "span_not_positive",
  "span_too_long",
]);
// Why a Recap attempt failed, was skipped, or was enqueued: closed codes only.
const SAFE_SESSION_RECAP_FAILURE_REASONS = new Set([
  "generation",
  "claim",
  "artifact",
  "empty",
  "disallowed_element",
  "unverified_quote",
]);
const SAFE_SESSION_RECAP_SKIP_REASONS = new Set(["in_progress", "terminal", "missing"]);
const SAFE_SESSION_RECAP_ENQUEUE_REASONS = new Set(["acceptance", "retry"]);
const SAFE_LOG_EVENT_REASON_VOCABULARIES = new Map<string, ReadonlySet<string>>([
  ["notifications.email_feedback_consumer_disabled", new Set(["queue_url_unset"])],
  // NO-933: why the notifications worker retained a queue message.
  [
    "notifications.queue_message_refused",
    new Set(["invalid", "unrecognized", "undecodable"]),
  ],
  ["worker.healthie_media_archive_skipped", SAFE_RECORDING_ARCHIVE_SKIP_REASONS],
  ["worker.healthie_recording_unverified", SAFE_RECORDING_IMPORT_REASONS],
  [
    "appointment_session.malformed_identity",
    SAFE_APPOINTMENT_SESSION_MALFORMED_REASONS,
  ],
  ["enhancement_claim.refused", SAFE_ENHANCEMENT_CLAIM_REFUSAL_REASONS],
  ["re_enhancement.refused", SAFE_RE_ENHANCEMENT_REFUSAL_REASONS],
  ["manual_soap_generation.refused", SAFE_MANUAL_SOAP_GENERATION_REFUSAL_REASONS],
  ["enhancement_claim.session_unresolved", SAFE_ENHANCEMENT_SESSION_UNRESOLVED_REASONS],
  ["soap_enhancement.ignored", SAFE_SOAP_ENHANCEMENT_IGNORED_REASONS],
  ["soap_enhancement.input_unavailable", SAFE_SOAP_ENHANCEMENT_INPUT_FAILURES],
  ["soap_enhancement.output_discarded", SAFE_SOAP_ENHANCEMENT_DISCARD_REASONS],
  ["prior_soap_notes.browse_failed", SAFE_PRIOR_SOAP_NOTE_INCOMPLETE_REASONS],
  ["prior_soap_notes.fetch_failed", SAFE_PRIOR_SOAP_NOTE_INCOMPLETE_REASONS],
  ["prior_soap_notes.selection_failed", SAFE_PRIOR_SOAP_NOTE_INCOMPLETE_REASONS],
  ["prior_soap_notes.response_refused", SAFE_PRIOR_SOAP_NOTE_RESPONSE_REFUSAL_REASONS],
  ["prior_soap_notes.answers_omitted", SAFE_PRIOR_SOAP_NOTE_OMISSION_REASONS],
  ["soap_enhancement.storage_failed", SAFE_SOAP_STORAGE_REASONS],
  ["session_recap.failed", SAFE_SESSION_RECAP_FAILURE_REASONS],
  ["session_recap.skipped", SAFE_SESSION_RECAP_SKIP_REASONS],
  ["session_recap.enqueued", SAFE_SESSION_RECAP_ENQUEUE_REASONS],
  ["healthie_write.service_key_fallback", SAFE_HEALTHIE_WRITE_FALLBACK_REASONS],
  ["healthie_write.rd_key_unreadable", SAFE_HEALTHIE_WRITE_FALLBACK_REASONS],
  ["healthie_write.rd_key_rejected", SAFE_HEALTHIE_WRITE_FALLBACK_REASONS],
  ["metrics_import.failed", SAFE_METRICS_IMPORT_FAILURE_REASONS],
  ["metrics_import.db_failed", SAFE_METRICS_IMPORT_FAILURE_REASONS],
  ["metrics_import.refused", SAFE_METRICS_IMPORT_REFUSAL_REASONS],
  ["metrics_import.identity_skipped", SAFE_METRICS_IMPORT_IDENTITY_SKIP_REASONS],
  ["supervisor_metrics.read_failed", SAFE_SUPERVISOR_METRICS_STORAGE_REASONS],
  ["supervisor_metrics.publish_failed", SAFE_SUPERVISOR_METRICS_STORAGE_REASONS],
  ["prior_soap_note_artifact.capture_failed", SAFE_PRIOR_SOAP_ARTIFACT_REASONS],
  ["transcript_source_sweep.failed", SAFE_TRANSCRIPT_SOURCE_SWEEP_REASONS],
  ["draft_ready.read_failed", SAFE_DRAFT_READY_FAILURE_REASONS],
  ["draft_ready.seen_write_failed", SAFE_DRAFT_READY_FAILURE_REASONS],
  ["transcript_source_cleanup.completed", SAFE_TRANSCRIPT_SOURCE_CLEANUP_REASONS],
  ["provider_smoke.preflight_failed", SAFE_PROVIDER_SMOKE_REASONS],
  ["provider_smoke.operation_failed", SAFE_PROVIDER_SMOKE_REASONS],
  ["soap_writeback.reconciled", SAFE_SOAP_WRITEBACK_REASONS],
  ["soap_writeback.reconciliation_skipped", SAFE_SOAP_WRITEBACK_REASONS],
  ["soap_writeback.reconciliation_read_failed", SAFE_SOAP_WRITEBACK_REASONS],
  ["soap_writeback.human_resolution_refused", SAFE_SOAP_WRITEBACK_REASONS],
  ["soap_writeback.intent_not_frozen", SAFE_SOAP_WRITEBACK_LOOP_REASONS],
  ["soap_writeback.delivery_ignored", SAFE_SOAP_WRITEBACK_LOOP_REASONS],
]);
// NO-474's generation failure vocabulary, ridden by NO-475's job telemetry.
const SAFE_SOAP_GENERATION_ERROR_CODES = new Set([
  "model_rejected",
  "model_unavailable",
  "output_invalid",
]);
// NO-774: why the worker could not load and freeze an Enhancement's inputs
// (`ENHANCEMENT_INPUT_FAILURE_CODES`). Closed codes; no identifiers or content.
const SAFE_SOAP_INPUT_LOAD_FAILURE_CODES = new Set([
  "appointment_identity_ambiguous",
  "appointment_identity_incomplete",
  "configuration_missing",
  "prior_note_configuration_missing",
  "prior_note_digest_mismatch",
  "prior_note_incomplete",
  "prior_note_storage_unavailable",
  "prior_note_unavailable",
  "session_missing",
  "snapshot_rejected",
  "transcript_missing",
]);
// NO-774: whether this delivery froze the Enhancement's snapshot or joined
// the one a racing delivery froze first.
const SAFE_SOAP_INPUT_LOAD_OUTCOMES = new Set(["frozen", "joined"]);
// NO-591 Session Recap attempt failure codes (`SESSION_RECAP_SAFE_ERROR_CODES`).
// NO-750 Patient Quick Note Healthie projection failure codes.
const SAFE_PATIENT_QUICK_NOTE_PROJECTION_CODES = new Set([
  "healthie_client_not_found",
  "healthie_patient_ambiguous",
  "healthie_patient_unresolved",
  "healthie_rejected",
  "healthie_unavailable",
  "lease_expired",
]);
// NO-870: the RD hours Healthie mirror's closed codes (`MIRROR_CODES` in
// always-ai's rd-hours-mirror/contracts.ts, proven equal by a package test).
const SAFE_RD_HOURS_MIRROR_CODES = new Set([
  "location_unconfigured",
  "provider_identity_missing",
  "provider_identity_ambiguous",
  "rd_inactive",
  "rd_schedule_off",
  "beyond_horizon",
  "lease_lost",
  "attempts_exhausted",
  "blocker_not_confirmed",
  "healthie_timeout",
  "healthie_transport",
  "healthie_rate_limited",
  "healthie_unavailable",
  "healthie_permission_denied",
  "healthie_rejected",
  "unexpected_error",
]);
const EXACT_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SAFE_RD_SCHEDULE_ACTION_KINDS = new Set([
  "book",
  "reschedule",
  "edit",
  "cancel",
  "delete",
]);
const SAFE_RD_SCHEDULE_ATTEMPT_STATUSES = new Set([
  "succeeded",
  "conflict",
  "failed",
  "superseded",
  "held",
  "settled",
]);
const SAFE_RD_SCHEDULE_LANES = new Set(["own", "supervised", "all"]);
// NO-872: an RD Schedule action attempt's closed failure and conflict codes.
const SAFE_RD_SCHEDULE_ACTION_CODES = new Set([
  "delivery_error",
  "healthie_read_incomplete",
  "slot_taken",
  "appointment_changed",
  "healthie_rejected",
  "healthie_permission_denied",
  "healthie_unavailable",
  "healthie_indeterminate",
  "series_partial",
  "invalid_request",
]);
// NO-832: the RD alerts Healthie gateway's closed failure codes.
const SAFE_RD_ALERT_HEALTHIE_CODES = new Set([
  "healthie_refused",
  "healthie_unauthorized",
  "healthie_unavailable",
  "healthie_invalid_response",
  "healthie_user_mismatch",
]);
// NO-833 shadow report Healthie read failure codes
// (`HEALTHIE_SENT_EMAILS_FAILURE_CODES`, printed with the `healthie_` prefix).
const SAFE_NOTIFICATIONS_SHADOW_REPORT_CODES = new Set([
  "healthie_access_denied",
  "healthie_rate_limited",
  "healthie_timeout",
  "healthie_network_error",
  "healthie_healthie_error",
  "healthie_unexpected_response",
  "healthie_page_limit",
  "healthie_unexpected_failure",
]);
const SAFE_SESSION_RECAP_ERROR_CODES = new Set([
  "model_unavailable",
  "model_rejected",
  "output_invalid",
  "transcript_unavailable",
  "configuration_unavailable",
  "generation_interrupted",
]);
const SAFE_ANALYSIS_REVISION_REASONS = new Set([
  "analysis_revision_not_current",
  "analysis_revision_not_found",
  "analysis_revision_required",
  "artifact_hash_mismatch",
  "artifact_missing",
  "cleanup_failed",
  "compensation_failed",
  "immutable_write_conflict",
]);
const SAFE_SYNC_ERROR_CODES = new Set([
  "sweep_page_limit",
  "sync_already_running",
  "sync_failed",
  "sync_skipped",
  "sync_stale_running",
  "healthie_conflict",
  "healthie_graphql_error",
  "healthie_http_error",
  "healthie_invalid_config",
  "healthie_invalid_input",
  "healthie_missing_credentials",
  "healthie_network_error",
  "healthie_not_found",
  "healthie_rate_limited",
  "healthie_timeout",
  "healthie_unexpected_response",
  "healthie_upstream_validation",
  // NO-469 transcript source adapter outcome codes.
  "healthie_rejected",
  "healthie_unavailable",
  // NO-472 appointment status reconciliation: the mutation echoed a different
  // Appointment or a status other than Occurred.
  "healthie_state_mismatch",
  // NO-476 incomplete prior-note reads halt Enhancement and carry a closed
  // reason separately.
  "healthie_incomplete",
  // NO-480 SOAP writeback outcomes.
  "healthie_create_indeterminate",
  "healthie_writeback_indeterminate",
  "healthie_chart_state_ambiguous",
  "healthie_appointment_patient_mismatch",
  "execution_guard_unavailable",
  "non_canonical_identifier",
  // NO-480 SOAP writeback: the request's digest does not cover its frozen
  // intent, Patient, Appointment, Mapping Revision, and SOAP text.
  "payload_digest_mismatch",
]);
const SAFE_SOAP_WRITEBACK_VERIFICATION_SOURCES = new Set(["prior_attempt", "create"]);
const ROW_DIFF_RE = /^-?\d{1,10}$/;
// NO-828: what one SES events queue message came to, the closed feedback kind,
// and its closed reason code (an SES bounce or complaint classification, or why
// the message was ignored or refused). Never an address or SES diagnostic text.
const SAFE_EMAIL_FEEDBACK_OUTCOMES = new Set([
  "recorded",
  "duplicate",
  "unmatched",
  "ignored",
  "refused",
]);
const SAFE_EMAIL_FEEDBACK_KINDS = new Set([
  "delivered",
  "hard_bounce",
  "soft_bounce",
  "complaint",
]);
const EMAIL_FEEDBACK_REASON_CODE_RE =
  /^(?:delivered|bounce_(?:permanent|transient|undetermined)_[a-z_]{1,40}|complaint_[a-z_]{1,20}|untracked_event|complaint_not_spam|not_an_ses_event|missing_field)$/;
const SAFE_EVENTS = new Set([
  "admin.starter_prompts_replaced",
  "analysis.completed",
  "analysis.failed",
  "analysis.started",
  "analysis_revision.integrity_failed",
  "analysis_stream.clear_failed",
  "analysis_stream.subscriber_open_failed",
  "analysis_stream.subscriber_read_failed",
  "analysis_stream.unreadable_messages",
  "analysis_stream.write_failed",
  "appointments.admin_scope_read",
  "appointments.detail_failed",
  "appointments.list_failed",
  "appointments.start_time_missing",
  "appointments.timezone_fallback",
  "appointments.unavailable",
  // NO-826: the book flow could not mark the appointment mirror as booked by
  // staff; the booking itself stands.
  "booking.booker_reconciliation_failed",
  "prior_soap_notes.route_failed",
  "prior_soap_notes.route_unavailable",
  "prior_soap_notes.selection_failed",
  "appointment_notes.read_failed",
  "appointment_notes.save_failed",
  "patient_quick_notes.read_failed",
  "patient_quick_notes.save_failed",
  "patient_quick_notes.flag_unreadable",
  // NO-847: the patient page's closed read failure.
  "patient_page.read_failed",
  // NO-868: the RD Schedule week. The flag evaluator threw (404), a read
  // failed with its closed code, or stored statuses outside the vocabulary
  // read as upcoming (a count only). No id, name, link or time rides on them.
  "rd_schedule.flag_unreadable",
  "rd_schedule.read_failed",
  "rd_schedule.unknown_status",
  // NO-852: a Quick facts or Goals read or save failed, with its closed code.
  // List text and the Patient id never ride on it.
  "quick_facts_and_goals.read_failed",
  "quick_facts_and_goals.save_failed",
  // NO-830: the in-app notification list's flag failed closed.
  "notifications.flag_unreadable",
  // NO-846: the patient-detail flag evaluator threw; the Patients list and
  // Client Red Flag routes answered 404.
  "patient_detail.flag_unreadable",
  // NO-925: the patient-messaging flag evaluator threw; the Messages routes
  // answered 404.
  "patient_messaging.flag_unreadable",
  // NO-927: the Healthie message ingest's closed outcome, and a Healthie read
  // refused for good (closed code). Ledger keys redact; no text or person id.
  "patient_messaging.ingest_settled",
  "patient_messaging.ingest_failed",
  // NO-925: a Messages read failed (bare; closed code only).
  "messages.read_failed",
  // NO-867: RD hours. The rd-schedule flag failed closed (the shared
  // rd_schedule.flag_unreadable above); a caller named an RD (lane plus opaque
  // actor and RD ids); a target had no usable Provider (lane, closed reason,
  // opaque actor id); a read or save failed (closed code). Hours, names and
  // visits never ride on any of them.
  "rd_hours.selected_scope",
  "rd_hours.unavailable",
  "rd_hours.read_failed",
  "rd_hours.save_failed",
  // NO-870: the RD hours Healthie mirror. Ids, counts and a closed code only.
  "rd_hours_mirror.enqueued",
  "rd_hours_mirror.enqueue_failed",
  "rd_hours_mirror.succeeded",
  "rd_hours_mirror.deferred",
  "rd_hours_mirror.retry_scheduled",
  "rd_hours_mirror.failed",
  // NO-871: RD blocked time. A read or save failed (closed code). Its scope
  // and flag lines are the RD hours ones above; block times and descriptions
  // never ride on any of them.
  "rd_blocks.read_failed",
  "rd_blocks.save_failed",
  "patient_quick_notes.projection_settled",
  "patient_quick_notes.projection_enqueue_failed",
  // NO-765: a failed Healthie quick_notes call, with closed refusal facts.
  "patient_quick_notes.healthie_call_failed",
  "patient_quick_notes.projection_failed",
  "patient_quick_notes.projection_conflict",
  "patient_quick_notes.healthie_sync_failed",
  // NO-784: one look at a Healthie edit inside the Quick Note block, with its
  // closed outcome. Note, projection and ledger ids redact.
  "patient_quick_notes.healthie_import_settled",
  // NO-784: a storage failure inside that import, with its closed code and the
  // SQLSTATE and constraint name only.
  "patient_quick_notes.healthie_import_failed",
  // NO-861: an Actual Duration request's enqueue, settlement, failure,
  // conflict and Healthie call failure. Minutes and Healthie ids never ride.
  "appointment_actual_duration.request_enqueue_failed",
  "appointment_actual_duration.write_settled",
  "appointment_actual_duration.write_failed",
  "appointment_actual_duration.write_conflict",
  "appointment_actual_duration.healthie_call_failed",
  // NO-872: an RD Schedule action's enqueue, settlement, failure, conflict,
  // Healthie call failure, and a read or submit that failed. Action and attempt
  // ids and closed codes only; names, notes, times and Healthie text never ride.
  "rd_schedule_actions.request_enqueue_failed",
  "rd_schedule_actions.write_settled",
  "rd_schedule_actions.write_failed",
  "rd_schedule_actions.write_conflict",
  "rd_schedule_actions.healthie_call_failed",
  "rd_schedule_actions.private_note_unavailable",
  "rd_schedule_actions.read_failed",
  "rd_schedule_actions.settle_failed",
  // NO-917: the Healthie copy of an NDD-owned visit: a retry scheduled, one that
  // could not be scheduled, the visit marked failed, and a mark that did not
  // save. Ids, a delay, an attempt number and closed codes only.
  "rd_schedule_actions.mirror_retry_scheduled",
  "rd_schedule_actions.mirror_retry_failed",
  "rd_schedule_actions.mirror_mark_failed",
  "rd_schedule_actions.mirror_failed",
  "rd_schedule_actions.mirror_left_pending",
  // Like appointments.unavailable: the lane and a closed reason only. The
  // selected-scope line names actor and subject ids, so it stays off this list.
  "rd_schedule_actions.unavailable",
  "rd_schedule_actions.submit_failed",
  // NO-846: a Client Red Flag read or Mark called failed, with its closed code.
  // Flag text, Patient and Session ids never ride on it.
  "patient_red_flags.read_failed",
  "patient_red_flags.mark_failed",
  "appointment_documentation.snapshot_failed",
  "appointment_documentation.enhance_failed",
  "appointment_documentation.enhance_unavailable",
  // NO-679 delivered-note Healthie click-through failure.
  "appointment_documentation.healthie_note_failed",
  "appointment_documentation.stream_failed",
  // NO-775 manual SOAP generation from a pasted Transcript.
  "appointment_documentation.manual_generation_failed",
  "appointment_documentation.manual_generation_unavailable",
  // NO-785 RD Re-enhance of a never-approved Draft.
  "appointment_documentation.re_enhance_failed",
  "appointment_documentation.re_enhance_unavailable",
  "re_enhancement.claimed",
  "re_enhancement.enqueue_failed",
  "re_enhancement.refused",
  "manual_soap_generation.requested",
  "manual_soap_generation.completed",
  "manual_soap_generation.refused",
  "manual_soap_generation.step_failed",
  "appointment_session.malformed_identity",
  "appointment_drafts.read_failed",
  "appointment_drafts.save_failed",
  "appointment_drafts.approval_failed",
  "appointment_drafts.copy_failed",
  "rd_schedule.details_link_copy_failed",
  "appointment_drafts.session_ambiguous",
  "api.request_failed",
  "auth.denied",
  "auth.provision_failed",
  "bedrock.error",
  "bedrock.request",
  "chat.citation_rejected",
  "chat.source_read",
  "chat.source_read_failed",
  "chat.thread_created",
  "chat_inference.error",
  "clinical_read.write_failed",
  // NO-556 published supervisor metrics storage lane.
  "supervisor_metrics.publish_failed",
  "supervisor_metrics.read_failed",
  // NO-557 Metrics Import Run. Counts, closed codes and digests only; the
  // importer's identity decisions live in the Import Reconciliation Report.
  "metrics_import.started",
  "metrics_import.sources_read",
  "metrics_import.source_rejected",
  "metrics_import.identities",
  "metrics_import.identity_skipped",
  "metrics_import.plan_built",
  "metrics_import.refused",
  "metrics_import.published",
  "metrics_import.report_written",
  "metrics_import.failed",
  "metrics_import.db_failed",
  "db.health_check_failed",
  "db.connection_failed",
  "db.slow_query",
  "enhancement_claim.claimed",
  "enhancement_claim.enqueue_failed",
  "enhancement_claim.refused",
  "enhancement_claim.safe_error_code_coerced",
  "enhancement_claim.session_unresolved",
  "enhancement_claim.stood_down",
  "enhancement_persistence.approval_refused",
  "enhancement_persistence.edit_conflict",
  "enhancement_persistence.edit_refused",
  "enhancement_persistence.snapshot_rejected",
  "enhancement_persistence.snapshot_reused",
  "enhancement_persistence.transition_refused",
  "prior_soap_note_artifact.capture_failed",
  "prior_soap_notes.configuration_failed",
  "feature_flags.client_error",
  "feature_flags.evaluation_failed",
  "feature_flags.not_configured",
  "healthie.appointment_sync_failed",
  "healthie.appointment_sync_parity_failed",
  "healthie.appointment_sync_tick_failed",
  "healthie.provider_context_failed",
  "healthie.reference_sync_failed",
  "healthie.reference_sync_tick_failed",
  "integration.request_failed",
  "people_link_bound",
  "people_link_denied",
  "people_link_failed",
  "people_read_crosswalk_miss",
  "presence.heartbeat_failed",
  "presence.read_failed",
  "presence.redis_recovered",
  // NO-428 automatic Enhancement: counts, one boolean, closed reasons.
  "auto_enhancement.sweep_completed",
  "draft_ready.read_failed",
  "draft_ready.seen_write_failed",
  "provider_smoke.operation_failed",
  "provider_smoke.preflight_failed",
  "redis.client_error",
  "server.action_failed",
  "server.queue_not_configured",
  "session_links.manual_link_refused",
  "session_links.manual_linked",
  "session_links.manual_unlinked",
  "sentry.smoke",
  "soap_enhancement.attempts_reaped",
  "soap_enhancement.attempt_reaped",
  "soap_enhancement.completed",
  "soap_enhancement.failed",
  "soap_enhancement.input_load_failed",
  "soap_enhancement.inputs_loaded",
  "soap_enhancement.ignored",
  "soap_enhancement.input_unavailable",
  "soap_enhancement.output_discarded",
  "soap_enhancement.progress_unavailable",
  "soap_enhancement.draft_text_unavailable",
  "soap_enhancement.reaper_failed",
  "soap_enhancement.reaper_recovered",
  "soap_enhancement.storage_failed",
  "soap_enhancement.started",
  "soap_generation.cancelled",
  "soap_generation.completed",
  "soap_generation.failed",
  "soap_generation.started",
  "session_recap.claimed",
  "session_recap.completed",
  "session_recap.enqueued",
  "session_recap.failed",
  "session_recap.flag_unreadable",
  "session_recap.progress_unavailable",
  "session_recap.skipped",
  "session_recap.superseded",
  "session_recap_generation.completed",
  "session_recap_generation.failed",
  "soap_workbench.cancelled",
  "soap_workbench.completed",
  "soap_workbench.failed",
  "soap_workbench.started",
  "scorecard_workbench.cancelled",
  "scorecard_workbench.completed",
  "scorecard_workbench.failed",
  "scorecard_workbench.started",
  "eval_worker.admission",
  "eval_worker.error",
  "eval_worker.lifecycle",
  "eval_worker.maintenance",
  "eval_worker.startup",
  "eval_worker.step",
  "webhook.dispatch_failed",
  "webhook.request_failed",
  "webhook_health.dispatch_failed",
  "worker.analysis_usage_gap",
  "worker.analysis_failure_suppressed",
  "notifications.email_feedback",
  "notifications.email_feedback_consumer_disabled",
  // NO-933: the notifications worker's process and refusal lines.
  "notifications.queue_message_refused",
  "notifications_worker.database_connected",
  "notifications_worker.database_unreachable",
  "notifications_worker.lifecycle",
  "notifications_worker.sqs_error",
  "notifications_worker.startup",
  "notifications_worker.task_protection_failed",
  "notifications_worker.task_protection_recovered",
  // NO-832: one failed Healthie call of the RD alerts switch (NO-765 shape).
  "notifications.rd_alert_healthie_failed",
  "worker.analysis_prompt_session_type_missing",
  "worker.analysis_revision_fenced",
  "worker.analysis_throttled",
  "worker.cleanup_failed",
  "worker.health_stale",
  "worker.health_write_failed",
  "worker.healthie_media_archive_skipped",
  "worker.healthie_media_duplicate",
  "worker.healthie_media_ignored",
  "worker.healthie_media_source_checked",
  "worker.healthie_media_stored",
  "worker.healthie_media_transcript_superseded",
  "worker.healthie_media_waiting",
  "worker.healthie_recording_delete_unconfirmed",
  "worker.healthie_recording_unverified",
  "worker.job_completed",
  "worker.job_failed",
  "worker.job_started",
  "worker.lifecycle",
  "worker.message_ignored",
  "worker.message_invalid",
  "worker.no_sections_detected",
  "worker.poll",
  "worker.poll_error",
  "worker.reaper_completed",
  "worker.reaper_conflict",
  "worker.reaper_lock_skipped",
  "worker.section_extract_failed",
  "worker.section_parse_failed",
  "worker.session_reaped",
  "worker.session_type_detect_failed",
  "worker.session_type_detect_throttled",
  "worker.session_type_detected",
  "worker.session_type_skipped",
  "worker.sqs_error",
  "worker.startup",
  "worker.status_update_failed",
  "worker.trace_open_failed",
  "worker.trace_unclosed",
  "worker.webhook_health_received",
  "transcript_sources.list_failed",
  "transcript_sources.fetch_failed",
  "transcript_persistence.version_accepted",
  "transcript_persistence.version_reused",
  "transcript_persistence.rejected",
  // NO-496 Transcript Source Sweep and immediate refusal cleanup.
  "transcript_source_sweep.completed",
  "transcript_source_sweep.previewed",
  "transcript_source_sweep.failed",
  "transcript_source_sweep.recovered",
  "transcript_source_sweep.lock_skipped",
  "transcript_source_cleanup.completed",
  "appointment_status.reconciled",
  "appointment_status.preserved",
  "appointment_status.reconcile_failed",
  "prior_soap_notes.browse_failed",
  "prior_soap_notes.fetch_failed",
  "prior_soap_notes.response_refused",
  "prior_soap_notes.answers_omitted",
  // A capture read that succeeded after a transient Healthie failure.
  "prior_soap_notes.read_recovered",
  // NO-783: an RD-initiated Healthie write that could not use the RD's key.
  "healthie_write.service_key_fallback",
  "healthie_write.rd_key_unreadable",
  "healthie_write.rd_key_rejected",
  // NO-480 SOAP writeback outcomes.
  "soap_writeback.delivered",
  "soap_writeback.reused",
  "soap_writeback.collision",
  "soap_writeback.failed",
  // NO-542 reconciliation of stuck writeback attempts: closed outcomes,
  // reasons, and counts only.
  "soap_writeback.reconciled",
  "soap_writeback.reconciliation_skipped",
  "soap_writeback.reconciliation_read_failed",
  "soap_writeback.reconciliation_completed",
  "soap_writeback.reconciliation_fence_skipped",
  "soap_writeback.human_resolution_recorded",
  "soap_writeback.human_resolution_refused",
  "soap_writeback.human_resolution_required",
  "soap_writeback.reconciliation_failed",
  "soap_writeback.reconciliation_recovered",
  // NO-765 durable writeback loop events that were dropped before because no
  // allowlist named them.
  "soap_writeback.intent_not_frozen",
  "soap_writeback.delivery_ignored",
  "soap_writeback.attempt_settled",
  "soap_writeback.retry_opened",
  "soap_writeback.retries_exhausted",
  "soap_writeback.enqueue_failed",
  "soap_writeback.retry_enqueue_failed",
  "soap_writeback.attempts_reaped",
  "soap_writeback.reaper_failed",
  "soap_writeback.reaper_recovered",
  "clinical_session_identity.resolved",
  "clinical_session_identity.unresolved",
  "clinical_session.created",
  "clinical_session.refused",
]);
// NO-417 verified Transcript event -> one versioned Clinical Session. Two
// closed vocabularies: which authoritative identity refused before any Session
// existed, and which durable conflict refused the Session itself. The media
// consumer's own waiting reasons join them so one operator view of this flow
// survives the scrubber intact.
const SAFE_CLINICAL_SESSION_REASONS = new Set([
  "provider_unknown",
  "provider_conflict",
  "appointment_unknown",
  "appointment_patient_unlinked",
  "patient_unrecognized",
  "appointment_type_unlinked",
  "appointment_claimed",
  "session_deleted",
  "identity_drifted",
  "acceptance_refused",
  "rd_mapping_missing",
  "required_source_missing",
  "recording_missing",
  "recording_processing",
  "recording_unavailable",
  "source_unavailable",
  // NO-763: every transcript Healthie listed downloaded with no cues.
  "transcript_empty",
  "identity_unresolved",
  // NO-811: Healthie refused to delete a recording NDD already holds verified.
  "recording_delete_refused",
  "resource_not_appointment",
  "clinical_session",
]);
// NO-470 versioned Transcript persistence: the stored source vocabulary and
// its closed refusal reasons.
const SAFE_TRANSCRIPT_SOURCE_TYPES = new Set([
  "healthie_realtime_capture",
  "healthie_processed_recording",
  "legacy_session_artifact",
  // NO-775: a Transcript an RD pasted because Healthie had none.
  "rd_pasted",
]);
const SAFE_TRANSCRIPT_PERSISTENCE_REASONS = new Set([
  "provenance_conflict",
  "duplicate",
  "session_missing",
  "provider_mismatch",
  "storage_missing",
  // NO-775: a paste refused because the Session already holds a Transcript.
  "transcript_exists",
]);
const SAFE_SERVICES = new Set(["healthie", "hubspot", "s3"]);
const SAFE_SURFACES = new Set([
  "analysis_chat",
  "analysis",
  "appointments",
  "appointment_notes",
  "patient_quick_notes",
  // NO-846 Client Red Flags.
  "patient_red_flags",
  // NO-852 Quick facts and Goals on the patient page.
  "patient_page",
  // NO-861 Appointment Actual Duration writes.
  "appointment_actual_duration",
  // NO-867 RD hours routes.
  "rd_hours",
  // NO-872 RD Schedule actions.
  "rd_schedule_actions",
  // NO-871 RD blocked time routes.
  "rd_blocks",
  "appointment_documentation",
  "appointment_drafts",
  "api",
  "auth",
  "bedrock",
  "booking",
  "chat",
  "chat_inference",
  "chat_source_flyover",
  "chat_source_read",
  "client",
  "clinical_documentation",
  "clipboard",
  "db",
  "database",
  "feature_flags",
  "health",
  "healthie_appointment_sync",
  "healthie_reference_sync_scheduler",
  "notifications",
  "observations",
  "presence",
  "queue_release",
  "scorecard_workbench",
  "server",
  "soap_workbench",
  "v1",
  "v2",
  "worker",
]);
const SAFE_TRIGGERS = new Set([
  "scheduled",
  // NO-745 / NO-763: why the media job found a Session's Analysis stale.
  "coverage_grew",
  "unread_after_error",
  "replaced_empty",
]);
const ERROR_TYPES = new Set([
  "AggregateError",
  "AnalysisCleanupError",
  "AnalysisRevisionArtifactIntegrityError",
  "AnalysisStatusUpdateError",
  "CallReviewAnalysisError",
  "Error",
  "EvalError",
  "EvaluationWorkerRuntimeError",
  "IntegrationError",
  "NddBookingWorkerRuntimeError",
  "AlwaysAiWorkerRuntimeError",
  "PayloadError",
  "RangeError",
  "ReferenceError",
  "SessionCompensationError",
  "SyntaxError",
  "TypeError",
  "URIError",
  "UnknownError",
  "VerificationError",
  "bigint",
  "boolean",
  "function",
  "number",
  "object",
  "string",
  "symbol",
  "undefined",
]);
const SAFE_BOOLEAN_KEYS = new Set([
  "analysis_root_present",
  "appointment_attached",
  // NO-428: the automatic Enhancement sweep read more candidates than its cap.
  "cap_reached",
  "defaulted",
  "durable",
  "fell_back",
  "handled",
  "linked_appointment",
  "linked_patient",
  "linked_provider",
  // NO-763: whether an empty transcript's delivery is re-checked on the queue.
  "recheck_scheduled",
  "released",
  "retriable",
  "retryable",
  "seeded_from_source",
  "smoke",
  "synthetic",
  "written",
]);
const SAFE_NUMBER_KEYS = new Set([
  "age_minutes",
  // NO-870: counts of Healthie rows one mirror run wrote.
  "availability_created",
  "availability_deleted",
  "blockers_created",
  "blockers_deleted",
  "blockers_orphaned",
  // NO-872: how many of an RD Schedule action's visits an attempt wrote.
  "written_count",
  "target_count",
  // NO-917: the seconds before a mirror retry and which attempt this is.
  "delay_seconds",
  "pending_count",
  "rd_schedule_attempt_number",
  // NO-745: how many Healthie transcripts the current Transcript Version and
  // the one the Analysis read each cover.
  "analyzed_coverage",
  "current_coverage",
  // NO-763: cue and byte counts of a downloaded Healthie transcript, and the
  // cue count of the Version it was compared with.
  "cue_count",
  "byte_length",
  // NO-775: UTF-8 byte lengths of a pasted Transcript and prior note. The
  // field names avoid the redacted `transcript` and `note` tokens.
  "pasted_byte_length",
  "pasted_prior_byte_length",
  "previous_cue_count",
  // NO-591 Session Recap: which of the Session's two attempts (1 or 2), and
  // how many blockquotes the validated Recap carried.
  "attempt_number",
  "quote_count",
  "candidate_count",
  // NO-428 automatic Enhancement sweep outcome counts. None identifies a
  // Session, Appointment, Patient or RD.
  "claimed_count",
  "already_active_count",
  "already_complete_count",
  "held_present_count",
  "held_grace_count",
  "held_unknown_count",
  "skipped_flag_off_count",
  "skipped_owner_unresolved_count",
  "owner_lookup_failed_count",
  "refused_count",
  "count",
  "deferred_count",
  "deleted_count",
  "durationMs",
  "duration_ms",
  // NO-774: the Enhance click's own time, and the worker's input load and
  // Healthie prior-Note read inside it.
  "claim_duration_ms",
  "input_load_duration_ms",
  "prior_soap_read_duration_ms",
  "failed_count",
  "flag_count",
  "grace_ms",
  "group_count",
  "known_closing_tag_count",
  "input_tokens",
  "listed_count",
  "note_count",
  "output_tokens",
  "pending_count",
  "prompt_count",
  "receive_count",
  "recording_count",
  "reaped_count",
  "reconciled_count",
  "referenced_count",
  "remaining_count",
  "reused_count",
  "retries_opened_count",
  "awaiting_human_count",
  "skipped_count",
  "rejected_count",
  "section_count",
  "skipped_count",
  "section_open_tag_count",
  "section_total",
  "stale_age_ms",
  "suppressed_count",
  "text_char_count",
  "thinking_char_count",
  "threshold_minutes",
  "token_count",
  "token_count_in",
  "unimported_count",
  "words_length",
  // NO-765: how many rejection messages a vendor returned.
  "vendor_rejection_count",
  // NO-557 Metrics Import Run. Every one is a count of rows, batches, sources
  // or findings; none of them identifies an RD.
  "sources",
  "resolved",
  "skipped",
  "registryOnly",
  "unlinked",
  "batches",
  "records",
  "findings",
]);
const HTTP_STATUS_NUMBER_KEYS = new Set([
  "status",
  "status_code",
  "upstreamStatus",
  "upstream_status",
]);
const SAFE_OPAQUE_ID_KEYS = new Set([
  "actor_id",
  "attempt_id",
  "chat_id",
  "entity_id",
  "external_auth_subject_id",
  "generation_id",
  "parent_span_id",
  "request_id",
  "run_id",
  "source_id",
  "span_id",
  "subject_id",
  "trace_id",
  "words_digest",
]);
const SAFE_SOURCES = new Set(["client", "healthie_ingest", "manual_tool", "server"]);
const SAFE_FROM_STATUSES = new Set([
  "analyzing",
  "pending",
  // NO-472 appointment status reconciliation source states.
  "scheduled",
  "checked_in",
]);
// The presence adapter's closed failure vocabulary (`PresenceFailureReason`
// in always-ai's redis-presence.ts) plus the heartbeat route's own reason.
// These ride the `reason` attribute of `presence.*` log events.
const SAFE_PRESENCE_FAILURE_REASONS = new Set([
  "command_failed",
  "connect_failed",
  "ownership_check_failed",
  "timeout",
]);
const SAFE_TRACE_FAULTS = new Set([
  "activation_expired",
  "activation_missing",
  "activation_pending",
  "activation_revoked",
  "classification_not_activated",
  "configuration_changed",
  "trace_contract_violation",
  "trace_store_unavailable",
]);
const SAFE_TRACE_FINALIZATION_FAILURES = new Set([
  "already_closed",
  "artifact_hash_mismatch",
  "artifact_missing",
  "artifact_unreadable",
  "database_unavailable",
]);
const SAFE_MODEL_PROVIDERS = new Set(["bedrock", "openai"]);
const SAFE_SOAP_WORKBENCH_MODELS = new Set(["claude-opus-4-6", "gpt-5-6"]);
// The Scorecard Workbench registry keys (NO-568); catalog keys, never provider ids.
const SAFE_SCORECARD_WORKBENCH_MODELS = new Set([
  "claude-opus-4-6",
  "gpt-5-6-sol",
  "gpt-5-6-terra",
  "gpt-5-6-luna",
  "gpt-6-astra",
]);
const SAFE_MODEL_FAILURE_CATEGORIES = new Set([
  "abnormal_finish",
  "access_denied",
  "auth",
  "content_filtered",
  "content_too_large",
  "credentials_expired",
  "empty_output",
  "model_timeout",
  "network",
  "output_truncated",
  "rate_limit",
  "service_unavailable",
  "stream_error",
  "throttling",
  "timeout",
  "unexpected_tool_call",
  "unknown",
  "validation",
  "zdr_config_invalid",
]);
const SAFE_SPAN_OPERATIONS = new Set([
  "browser",
  "browser.cache",
  "browser.connect",
  "browser.dns",
  "browser.redirect",
  "browser.request",
  "browser.response",
  "db",
  "db.query",
  "db.sql.query",
  "http.client",
  "http.request",
  "http.server",
  "measure",
  "middleware.express",
  "navigation",
  "pageload",
  "request_handler.express",
  "resource",
  "resource.css",
  "resource.fetch",
  "resource.img",
  "resource.link",
  "resource.other",
  "resource.script",
  "resource.xmlhttprequest",
  "router.express",
  "task",
  "ui.action.click",
  "ui.action.submit",
]);
const SAFE_SPAN_STATUSES = new Set([
  "aborted",
  "already_exists",
  "cancelled",
  "data_loss",
  "deadline_exceeded",
  "failed_precondition",
  "internal_error",
  "invalid_argument",
  "not_found",
  "ok",
  "out_of_range",
  "permission_denied",
  "resource_exhausted",
  "unauthenticated",
  "unavailable",
  "unimplemented",
  "unknown_error",
]);
const BREADCRUMB_CATEGORIES = new Set([
  "console",
  "fetch",
  "http",
  "navigation",
  "sentry.event",
  "sentry.transaction",
  "ui.click",
  "ui.input",
  "ui.submit",
  "xhr",
]);
const BREADCRUMB_TYPES = new Set([
  "default",
  "debug",
  "error",
  "http",
  "info",
  "navigation",
  "query",
  "user",
]);
const SAFE_PLATFORMS = new Set(["javascript", "node"]);
const EVENT_ID_RE = /^[a-f0-9]{32}$/i;
const OPAQUE_USER_ID_RE = /^(?:[A-Za-z]+_[A-Za-z0-9_-]+|[a-f0-9-]{16,})$/i;
const SAFE_OBJECT_KEY_RE = /^[A-Za-z][A-Za-z0-9_.-]{0,127}$/;
// Code artifacts, not runtime data: bundle/module paths (query stripped, no
// whitespace), JS callable names, error class names, SDK mechanism enums, and
// dotted logger names. Free-form prose never matches any of these shapes.
const SAFE_STACK_PATH_CHARSET_RE = /^[A-Za-z0-9_@./:<>[\]-]{1,512}$/;
const SAFE_FUNCTION_RE = /^(?:new |async |get |set )?[\w$.<>[\]]{1,128}$/;
const ERROR_TYPE_IDENTIFIER_RE = /^[A-Za-z][A-Za-z0-9_$]{0,63}$/;
const MECHANISM_RE = /^[a-z][a-z0-9_.]{0,63}$/;
const LOGGER_RE = /^[A-Za-z][A-Za-z0-9_.:/#-]{0,127}$/;

/**
 * Grouping keys set on purpose by NDD code. Each is a static string, never
 * runtime data. A fingerprint made only of these keys survives scrubbing; any
 * other fingerprint is redacted whole. Without its own fingerprint an event
 * groups by stack trace, so two reports from one call site share an issue
 * (and that issue's priority) whatever their messages say.
 */
export const HEALTHIE_RECORDING_NOT_FOUND_FINGERPRINT = [
  "healthie-recording-delete-refused",
  "recording_not_found",
] as const;

/**
 * A Healthie call that timed out, lost its connection, was rate limited or got
 * a 5xx. Each operation is its own issue, apart from the failures that are
 * ours to fix (a configuration fault, an unclassified throw), so its warning
 * level keeps it off the high-priority pager.
 */
export const HEALTHIE_TRANSIENT_FINGERPRINTS = {
  "prior_soap_notes.browse_recent_signed_notes": [
    "healthie-transient",
    "prior_soap_notes.browse_recent_signed_notes",
  ],
  "prior_soap_notes.fetch_latest_qualifying_note": [
    "healthie-transient",
    "prior_soap_notes.fetch_latest_qualifying_note",
  ],
  "prior_soap_notes.fetch_recent_signed_note": [
    "healthie-transient",
    "prior_soap_notes.fetch_recent_signed_note",
  ],
  "appointment_status.read": ["healthie-transient", "appointment_status.read"],
  "appointment_status.update": ["healthie-transient", "appointment_status.update"],
  "transcript_sources.list_completed_sources": [
    "healthie-transient",
    "transcript_sources.list_completed_sources",
  ],
  "transcript_sources.fetch_source": [
    "healthie-transient",
    "transcript_sources.fetch_source",
  ],
} as const;
export type HealthieTransientOperation = keyof typeof HEALTHIE_TRANSIENT_FINGERPRINTS;
const SAFE_FINGERPRINT_KEYS: ReadonlySet<string> = new Set<string>([
  // A Healthie recording delete refused with "Recording not found." is its own
  // issue, so its warning level keeps that issue off the high-priority pager.
  ...HEALTHIE_RECORDING_NOT_FOUND_FINGERPRINT,
  ...Object.values(HEALTHIE_TRANSIENT_FINGERPRINTS).flat(),
]);
const LONG_STRING_THRESHOLD = 500;
const LONG_STRING_KEEP = 100;
const SAFE_FRAME_NUMBER_MAX = 10_000_000;
const EMAIL_RE = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const PHONE_RE = /\b(?:\+?1[\s.-]?)?(?:\(?\d{3}\)?[\s.-]?)\d{3}[\s.-]?\d{4}\b/g;
const DOB_RE = /\b(?:\d{1,2}[/-]){2}\d{2,4}\b/g;
const ADDRESS_RE =
  /\b\d{1,6}\s+[A-Za-z0-9.'-]+(?:\s+[A-Za-z0-9.'-]+){0,5}\s+(?:Apt|Apartment|Ave|Avenue|Blvd|Boulevard|Circle|Cir|Court|Ct|Drive|Dr|Lane|Ln|Parkway|Pkwy|Place|Pl|Road|Rd|Street|St|Suite|Unit|Way)\b/gi;
const URL_LIKE_RE = /\bhttps?:\/\/[^\s"'<>]+|\/api\/[^\s"'<>]+/gi;
const UUID_RE = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;

const REDACTED_VALUE = "[REDACTED]";
const URL_HASH_PREFIX = "sha256:";
const URL_HASH_VALUE_RE = /^sha256:[a-f0-9]{64}$/;
const S3_BUCKET_RE = /^[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]$/;
const AWS_REGION_RE = /^[a-z]{2}(?:-[a-z0-9]+)+-\d$/;
const ERROR_DUMP_KEY_RE =
  /^(?:always-ai|ndd-booking|ndd-admin|ndd-webhooks)\/\d{4}\/\d{2}\/\d{2}\/[a-f0-9]{32}\.json$/;
const ERROR_DUMP_CONSOLE_ORIGIN = "https://s3.console.aws.amazon.com";
const ERROR_DUMP_OBJECT_PATH_RE = /^\/s3\/object\/([^/]+)$/;
const REDACTED_TRACE_LABEL = `${URL_HASH_PREFIX}${"0".repeat(64)}`;
const SCRUBBED_SPAN_DESCRIPTIONS = new WeakMap<SpanJSON, string>();
const SHA256_INITIAL_HASH = [
  0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab,
  0x5be0cd19,
];
const SHA256_ROUND_CONSTANTS = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4,
  0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe,
  0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f,
  0x4a7484aa, 0x5cb0a9dc, 0x76f988da, 0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7,
  0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc,
  0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
  0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070, 0x19a4c116,
  0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7,
  0xc67178f2,
];

function rightRotate(value: number, bits: number): number {
  return (value >>> bits) | (value << (32 - bits));
}

// Sentry breadcrumb processing is synchronous in browser and Node runtimes.
function sha256(value: string): string {
  const bytes = new TextEncoder().encode(value);
  const bitLength = bytes.length * 8;
  const paddedLength = Math.ceil((bytes.length + 9) / 64) * 64;
  const padded = new Uint8Array(paddedLength);
  const view = new DataView(padded.buffer);
  const words = new Uint32Array(64);
  const hash = [...SHA256_INITIAL_HASH];

  padded.set(bytes);
  padded[bytes.length] = 0x80;
  view.setUint32(paddedLength - 8, Math.floor(bitLength / 2 ** 32));
  view.setUint32(paddedLength - 4, bitLength >>> 0);

  for (let offset = 0; offset < paddedLength; offset += 64) {
    for (let index = 0; index < 16; index += 1) {
      words[index] = view.getUint32(offset + index * 4);
    }

    for (let index = 16; index < 64; index += 1) {
      const first = words[index - 15];
      const second = words[index - 2];
      const sigma0 = rightRotate(first, 7) ^ rightRotate(first, 18) ^ (first >>> 3);
      const sigma1 =
        rightRotate(second, 17) ^ rightRotate(second, 19) ^ (second >>> 10);
      words[index] = (words[index - 16] + sigma0 + words[index - 7] + sigma1) >>> 0;
    }

    let [a, b, c, d, e, f, g, h] = hash;

    for (let index = 0; index < 64; index += 1) {
      const sigma1 = rightRotate(e, 6) ^ rightRotate(e, 11) ^ rightRotate(e, 25);
      const choice = (e & f) ^ (~e & g);
      const temp1 =
        (h + sigma1 + choice + SHA256_ROUND_CONSTANTS[index] + words[index]) >>> 0;
      const sigma0 = rightRotate(a, 2) ^ rightRotate(a, 13) ^ rightRotate(a, 22);
      const majority = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (sigma0 + majority) >>> 0;

      h = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }

    hash[0] = (hash[0] + a) >>> 0;
    hash[1] = (hash[1] + b) >>> 0;
    hash[2] = (hash[2] + c) >>> 0;
    hash[3] = (hash[3] + d) >>> 0;
    hash[4] = (hash[4] + e) >>> 0;
    hash[5] = (hash[5] + f) >>> 0;
    hash[6] = (hash[6] + g) >>> 0;
    hash[7] = (hash[7] + h) >>> 0;
  }

  return hash.map((part) => part.toString(16).padStart(8, "0")).join("");
}

function pathnameFromUrl(value: string): string {
  try {
    return new URL(value, "https://nodietdietitian.com").pathname;
  } catch {
    return value.split(/[?#]/, 1)[0] ?? value;
  }
}

function hashPathLikeLabel(value: string): string {
  return `${URL_HASH_PREFIX}${sha256(pathnameFromUrl(value))}`;
}

function scrubString(value: string): string {
  return value
    .replace(URL_LIKE_RE, pathnameFromUrl)
    .replace(UUID_RE, REDACTED_VALUE)
    .replace(EMAIL_RE, REDACTED_VALUE)
    .replace(PHONE_RE, REDACTED_VALUE)
    .replace(DOB_RE, REDACTED_VALUE)
    .replace(ADDRESS_RE, REDACTED_VALUE);
}

function clipLong(value: string): string {
  return value.length > LONG_STRING_THRESHOLD
    ? `${value.slice(0, LONG_STRING_KEEP)}...[clipped]`
    : value;
}

function scrubFreeformString(value: string): string {
  return clipLong(scrubString(value));
}

// Pattern-based scrub for reviewed freeform surfaces (error messages, console
// breadcrumb arguments). Residual prose risk is accepted under the signed
// Sentry BAA — see ADR-0036 amendment. Structured surfaces (tags, extra,
// contexts, spans) stay on the default-deny scrubUnknown path.
function scrubFreeform(value: unknown, seen: WeakSet<object> = new WeakSet()): unknown {
  if (typeof value === "string") {
    return scrubFreeformString(value);
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : REDACTED_VALUE;
  }

  if (typeof value === "boolean" || value === null || value === undefined) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => scrubFreeform(item, seen));
  }

  if (typeof value !== "object") {
    return REDACTED_VALUE;
  }

  if (seen.has(value)) {
    return "[Circular]";
  }

  seen.add(value);

  return Object.fromEntries(
    Object.entries(value).map(([entryKey, entryValue], index) => [
      SAFE_OBJECT_KEY_RE.test(entryKey) ? entryKey : `redacted_${index}`,
      scrubFreeform(entryValue, seen),
    ])
  );
}

function sanitizeStackPath(value: string): string {
  const stripped = value.split(/[?#]/, 1)[0] ?? value;

  return SAFE_STACK_PATH_CHARSET_RE.test(stripped) && stripped.match(EMAIL_RE) === null
    ? stripped
    : REDACTED_VALUE;
}

type StringAcceptor = (value: string) => boolean;

const inSet =
  (allowlist: ReadonlySet<string>): StringAcceptor =>
  (value) =>
    allowlist.has(value);
const matches =
  (pattern: RegExp): StringAcceptor =>
  (value) =>
    pattern.test(value);
const inAnySet =
  (...allowlists: ReadonlySet<string>[]): StringAcceptor =>
  (value) =>
    allowlists.some((allowlist) => allowlist.has(value));

// A comma-joined list of names, each accepted on its own.
const listsOnly =
  (acceptsName: StringAcceptor): StringAcceptor =>
  (value) => {
    const names = value.split(",");

    return names.length <= MAX_LISTED_NAMES && names.every(acceptsName);
  };

// Keys whose values are validated before the pattern scrub, because
// `scrubString` would mangle them: hex hashes and UUID-shaped opaque ids, plus
// enums that name code rather than data. Breadcrumbs pass through the scrubber
// twice (beforeBreadcrumb, then again inside scrubEvent); the hash this
// scrubber emits must survive the re-scrub.
const VERBATIM_STRING_ACCEPTORS = new Map<string, StringAcceptor>([
  ...[...SAFE_OPAQUE_ID_KEYS].map((key): [string, StringAcceptor] => [
    key,
    matches(OPAQUE_USER_ID_RE),
  ]),
  ["url_hash", matches(URL_HASH_VALUE_RE)],
  ["error_type", inSet(ERROR_TYPES)],
  ["error_dump_status", inSet(SAFE_ERROR_DUMP_STATUSES)],
  // NO-765: the alert-to-dump link on a failure log line, and the vendor's
  // closed refusal facts.
  ["sentry_event_id", matches(EVENT_ID_RE)],
  ["error_dump_url", (value) => isApprovedErrorDumpUrl(value)],
  ["vendor_operation", matches(VENDOR_OPERATION_RE)],
  // NO-784: a Postgres SQLSTATE, and the name of a Patient Quick Note
  // constraint or index. Both are schema facts, never row data.
  ["sql_state", matches(SQLSTATE_RE)],
  // NO-828: the SES feedback kind and its closed reason code.
  ["feedback", inSet(SAFE_EMAIL_FEEDBACK_KINDS)],
  ["reason_code", matches(EMAIL_FEEDBACK_REASON_CODE_RE)],
  // NO-784: which caller saw the Healthie edit, exactly as the app logs it.
  ["detectedBy", inSet(SAFE_QUICK_NOTE_IMPORT_DETECTORS)],
  ["constraint", matches(PATIENT_QUICK_NOTE_CONSTRAINT_RE)],
  ["vendor_code", inSet(SAFE_VENDOR_CODES)],
  ["vendor_reason", inSet(SAFE_VENDOR_REASONS)],
  ["vendor_fields", listsOnly((name) => SAFE_VENDOR_FIELDS.has(name))],
  ["query_keys", listsOnly((name) => QUERY_KEY_RE.test(name))],
  ["attempt_status", inSet(SAFE_SOAP_WRITEBACK_ATTEMPT_STATUSES)],
  ["write_credential", inSet(SAFE_HEALTHIE_WRITE_CREDENTIALS)],
  ["store_code", inSet(SAFE_HEALTHIE_KEY_STORE_CODES)],
  // NO-872 RD Schedule action lines: the action and attempt ids (random
  // UUIDs, the RD-facing findable id), the kind, the attempt status and the
  // scope lane. Keys carry the rd_schedule_ prefix so no other event reuses them.
  ["rd_schedule_action_id", matches(EXACT_UUID_RE)],
  ["rd_schedule_attempt_id", matches(EXACT_UUID_RE)],
  ["rd_schedule_action_kind", inSet(SAFE_RD_SCHEDULE_ACTION_KINDS)],
  ["rd_schedule_attempt_status", inSet(SAFE_RD_SCHEDULE_ATTEMPT_STATUSES)],
  ["rd_schedule_lane", inSet(SAFE_RD_SCHEDULE_LANES)],
]);

// `reason` rides many event families. The exception path has no event context,
// so a tag value survives if any reviewed vocabulary holds it; beforeSendLog
// narrows the log path per event through SAFE_LOG_EVENT_REASON_VOCABULARIES.
const acceptsAnySafeReason = inAnySet(
  SAFE_MESSAGE_INGEST_SKIP_REASONS,
  SAFE_TRACE_FINALIZATION_FAILURES,
  SAFE_ANALYSIS_REVISION_REASONS,
  SAFE_PRESENCE_FAILURE_REASONS,
  SAFE_PRIOR_SOAP_NOTE_REASONS,
  SAFE_ENHANCEMENT_PERSISTENCE_REASONS,
  SAFE_ENHANCEMENT_REASONS,
  SAFE_TRANSCRIPT_PERSISTENCE_REASONS,
  SAFE_SOAP_WRITEBACK_REASONS,
  SAFE_SOAP_WRITEBACK_LOOP_REASONS,
  SAFE_HEALTHIE_WRITE_FALLBACK_REASONS,
  SAFE_PROVIDER_SMOKE_REASONS,
  SAFE_CLINICAL_SESSION_REASONS,
  SAFE_RECORDING_IMPORT_REASONS,
  SAFE_RECORDING_ARCHIVE_SKIP_REASONS,
  SAFE_APPOINTMENT_SELECTION_DENIALS,
  SAFE_RD_HOURS_DENIALS,
  SAFE_SUPERVISOR_METRICS_STORAGE_REASONS,
  SAFE_METRICS_IMPORT_FAILURE_REASONS,
  SAFE_METRICS_IMPORT_REFUSAL_REASONS,
  SAFE_METRICS_IMPORT_IDENTITY_SKIP_REASONS
);

// Keys whose values are validated after the pattern scrub. A key absent from
// both tables redacts.
const SCRUBBED_STRING_ACCEPTORS = new Map<string, StringAcceptor>([
  ["method", matches(METHOD_RE)],
  ["op", inSet(SAFE_SPAN_OPERATIONS)],
  ["status", (value) => STATUS_RE.test(value) || SAFE_SPAN_STATUSES.has(value)],
  ["status_code", matches(STATUS_RE)],
  ["upstream_status", matches(STATUS_RE)],
  ["retryable", inSet(BOOLEAN_STRINGS)],
  ["smoke", inSet(BOOLEAN_STRINGS)],
  ["check", inSet(SAFE_CHECKS)],
  ["availability_reason", inSet(SAFE_APPOINTMENT_AVAILABILITY_REASONS)],
  ["timezone_fallback_reason", inSet(SAFE_APPOINTMENT_TIMEZONE_FALLBACK_REASONS)],
  ["code", inAnySet(SAFE_CODES, SAFE_METRICS_IMPORT_REJECTION_CODES)],
  // NO-557 Metrics Import Run attributes: each is a closed vocabulary or a
  // digest, and none of them can carry an RD identity.
  ["stage", inSet(SAFE_METRICS_IMPORT_STAGES)],
  ["port", inSet(SAFE_METRICS_IMPORT_PORTS)],
  ["sink", inSet(SAFE_METRICS_IMPORT_SINKS)],
  ["planSha256", matches(SHA256_RE)],
  [
    "safe_error_code",
    inAnySet(
      SAFE_SYNC_ERROR_CODES,
      SAFE_SOAP_GENERATION_ERROR_CODES,
      SAFE_SOAP_INPUT_LOAD_FAILURE_CODES,
      SAFE_SESSION_RECAP_ERROR_CODES,
      SAFE_PATIENT_QUICK_NOTE_PROJECTION_CODES,
      SAFE_RD_HOURS_MIRROR_CODES,
      SAFE_RD_SCHEDULE_ACTION_CODES,
      SAFE_RD_ALERT_HEALTHIE_CODES,
      SAFE_NOTIFICATIONS_SHADOW_REPORT_CODES
    ),
  ],
  ["verification_source", inSet(SAFE_SOAP_WRITEBACK_VERIFICATION_SOURCES)],
  ["failure_code", inSet(SAFE_FAILURE_CODES)],
  ["failure_category", inSet(SAFE_MODEL_FAILURE_CATEGORIES)],
  ["model_key", inAnySet(SAFE_SOAP_WORKBENCH_MODELS, SAFE_SCORECARD_WORKBENCH_MODELS)],
  ["provider", inSet(SAFE_MODEL_PROVIDERS)],
  ["flow", inSet(SAFE_FLOWS)],
  ["job_slug", inSet(SAFE_JOB_SLUGS)],
  ["from_status", inSet(SAFE_FROM_STATUSES)],
  ["fault", inSet(SAFE_TRACE_FAULTS)],
  ["logger", matches(LOGGER_RE)],
  ["route", inSet(SAFE_DENIED_ROUTES)],
  ["lane", inAnySet(SAFE_APPOINTMENT_SCOPE_LANES, SAFE_RD_HOURS_LANES)],
  ["operation", inSet(SAFE_OPERATIONS)],
  ["row_diff", matches(ROW_DIFF_RE)],
  ["event", inSet(SAFE_EVENTS)],
  ["route_target", inSet(ROUTE_TARGETS)],
  ["service", inSet(SAFE_SERVICES)],
  ["surface", inSet(SAFE_SURFACES)],
  ["source", inSet(SAFE_SOURCES)],
  ["trigger", inSet(SAFE_TRIGGERS)],
  ["action", inSet(SAFE_ACTIONS)],
  ["artifact", inSet(SAFE_ARTIFACTS)],
  ["category", inSet(SAFE_ANALYSIS_FAILURE_CATEGORIES)],
  ["error_category", inSet(SAFE_ANALYSIS_FAILURE_CATEGORIES)],
  ["phase", inSet(SAFE_ANALYSIS_REVISION_PHASES)],
  ["analysis_revision_creation_mode", inSet(SAFE_ANALYSIS_REVISION_CREATION_MODES)],
  ["source_type", inSet(SAFE_TRANSCRIPT_SOURCE_TYPES)],
  ["runtime", inSet(SAFE_RUNTIMES)],
  [
    "outcome",
    inAnySet(
      SAFE_TRANSCRIPT_SOURCE_CLEANUP_OUTCOMES,
      SAFE_SOAP_WRITEBACK_SETTLEMENT_OUTCOMES,
      SAFE_METRICS_IMPORT_PUBLICATION_OUTCOMES,
      SAFE_RECORDING_IMPORT_OUTCOMES,
      SAFE_TRANSCRIPT_SUPERSEDED_OUTCOMES,
      SAFE_TRANSCRIPT_SOURCE_CHECK_OUTCOMES,
      SAFE_SOAP_INPUT_LOAD_OUTCOMES,
      SAFE_MANUAL_SOAP_GENERATION_OUTCOMES,
      SAFE_PATIENT_QUICK_NOTE_IMPORT_OUTCOMES,
      SAFE_MESSAGE_INGEST_OUTCOMES,
      SAFE_EMAIL_FEEDBACK_OUTCOMES
    ),
  ],
  ["timing_outcome", inSet(SAFE_TRANSCRIPT_TIMING_OUTCOMES)],
  ["reason", acceptsAnySafeReason],
]);

function scrubSafeString(value: string, key?: string): string {
  if (key === undefined) {
    return REDACTED_VALUE;
  }

  const acceptsVerbatim = VERBATIM_STRING_ACCEPTORS.get(key);

  if (acceptsVerbatim) {
    return acceptsVerbatim(value) ? value : REDACTED_VALUE;
  }

  const acceptsScrubbed = SCRUBBED_STRING_ACCEPTORS.get(key);
  const scrubbed = scrubString(value);

  if (acceptsScrubbed && acceptsScrubbed(scrubbed)) {
    return scrubbed;
  }

  return REDACTED_VALUE;
}

function isBareConsoleUrl(url: URL): boolean {
  const carriesExtras = Boolean(url.username || url.password || url.hash);

  return url.origin === ERROR_DUMP_CONSOLE_ORIGIN && !carriesExtras;
}

function isErrorDumpBucket(bucket: string, region: string): boolean {
  return (
    S3_BUCKET_RE.test(bucket) &&
    bucket.startsWith(`${region}-`) &&
    bucket.endsWith("-ndd-error-dumps")
  );
}

function isApprovedErrorDumpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    const pathMatch = url.pathname.match(ERROR_DUMP_OBJECT_PATH_RE);

    if (!pathMatch || !isBareConsoleUrl(url)) {
      return false;
    }

    if ([...url.searchParams.keys()].join(",") !== "region,prefix") {
      return false;
    }

    const bucket = decodeURIComponent(pathMatch[1]);
    const region = url.searchParams.get("region") ?? "";
    const prefix = url.searchParams.get("prefix") ?? "";

    return (
      AWS_REGION_RE.test(region) &&
      isErrorDumpBucket(bucket, region) &&
      ERROR_DUMP_KEY_RE.test(prefix)
    );
  } catch {
    return false;
  }
}

function isHttpStatusNumber(value: number): boolean {
  return Number.isInteger(value) && value >= 100 && value <= 599;
}

function scrubSafeNumber(value: number, key?: string): number | string {
  if (!Number.isFinite(value)) {
    return REDACTED_VALUE;
  }

  if (key && SAFE_NUMBER_KEYS.has(key)) {
    return value >= 0 ? value : REDACTED_VALUE;
  }

  if (key && HTTP_STATUS_NUMBER_KEYS.has(key)) {
    return isHttpStatusNumber(value) ? value : REDACTED_VALUE;
  }

  return REDACTED_VALUE;
}

function shouldRedactKey(key?: string): boolean {
  if (!key) {
    return false;
  }

  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[^A-Za-z0-9]+/)
    .some((token) => FORBIDDEN_KEY_TOKENS.has(token.toLowerCase()));
}

function scrubUnknown(
  value: unknown,
  key?: string,
  seen: WeakSet<object> = new WeakSet()
): unknown {
  if (typeof value === "number" && key === "stale_age_ms") {
    return scrubSafeNumber(value, key);
  }

  if (shouldRedactKey(key)) {
    return REDACTED_VALUE;
  }

  if (typeof value === "string") {
    return scrubSafeString(value, key);
  }

  if (Array.isArray(value)) {
    return value.map((item) => scrubUnknown(item, undefined, seen));
  }

  if (typeof value === "number") {
    return scrubSafeNumber(value, key);
  }

  if (typeof value === "boolean") {
    return key && SAFE_BOOLEAN_KEYS.has(key) ? value : REDACTED_VALUE;
  }

  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value !== "object") {
    return REDACTED_VALUE;
  }

  if (seen.has(value)) {
    return "[Circular]";
  }

  seen.add(value);

  return Object.fromEntries(
    Object.entries(value).map(([entryKey, entryValue], index) => [
      SAFE_OBJECT_KEY_RE.test(entryKey) ? entryKey : `redacted_${index}`,
      scrubUnknown(entryValue, entryKey, seen),
    ])
  );
}

function isSafeFrameNumber(value: number): boolean {
  return Number.isInteger(value) && value >= 0 && value <= SAFE_FRAME_NUMBER_MAX;
}

function sanitizeStackFrame(frame: StackFrame): void {
  if (frame.filename) {
    frame.filename = sanitizeStackPath(frame.filename);
  }

  if (frame.abs_path) {
    frame.abs_path = sanitizeStackPath(frame.abs_path);
  }

  if (frame.function && !SAFE_FUNCTION_RE.test(frame.function)) {
    delete frame.function;
  }

  delete frame.module;
  delete frame.context_line;
  delete frame.pre_context;
  delete frame.post_context;
  delete frame.instruction_addr;
  delete frame.addr_mode;
  delete frame.debug_id;
  delete frame.module_metadata;
  delete frame.vars;

  if (frame.lineno !== undefined && !isSafeFrameNumber(frame.lineno)) {
    delete frame.lineno;
  }

  if (frame.colno !== undefined && !isSafeFrameNumber(frame.colno)) {
    delete frame.colno;
  }

  if (frame.platform && !SAFE_PLATFORMS.has(frame.platform)) {
    frame.platform = REDACTED_VALUE;
  }
}

function sanitizeRequest(event: Event): void {
  if (!event.request) {
    return;
  }

  const urlHash =
    typeof event.request.url === "string"
      ? hashPathLikeLabel(event.request.url)
      : undefined;

  delete event.request.data;
  delete event.request.cookies;
  delete event.request.env;
  delete event.request.headers;
  delete event.request.query_string;

  if (urlHash) {
    event.request.url = urlHash;
  } else {
    delete event.request.url;
  }

  if (event.request.method && !METHOD_RE.test(event.request.method)) {
    event.request.method = REDACTED_VALUE;
  }
}

function sanitizeNetworkBreadcrumb(crumb: Breadcrumb): void {
  if (!crumb.data) {
    return;
  }

  const next: Record<string, unknown> = { ...crumb.data };
  const urlHash =
    typeof next.url === "string" ? hashPathLikeLabel(next.url) : undefined;
  delete next.body;
  delete next.request_body;
  delete next.requestBody;
  delete next.response_body;
  delete next.responseBody;
  delete next.headers;
  delete next.request_headers;
  delete next.requestHeaders;
  delete next.response_headers;
  delete next.responseHeaders;
  delete next.cookies;
  delete next.url;
  // Node's outgoing http/fetch breadcrumbs name the method `http.method` and
  // carry the query string and fragment beside the sanitized URL. Only the
  // method and the query parameter names survive.
  const method = next.method ?? next["http.method"];
  const queryKeys = queryKeyNames(next["http.query"]);
  delete next["http.method"];
  delete next["http.query"];
  delete next["http.fragment"];

  if (method !== undefined) {
    next.method = method;
  }

  if (queryKeys) {
    next.query_keys = queryKeys;
  }

  const scrubbed = scrubUnknown(next) as Breadcrumb["data"];
  crumb.data = urlHash ? { ...scrubbed, url_hash: urlHash } : scrubbed;
}

function queryKeyNames(search: unknown): string | undefined {
  if (typeof search !== "string") {
    return undefined;
  }

  const names = [...new Set(new URLSearchParams(search).keys())]
    .filter((name) => QUERY_KEY_RE.test(name))
    .sort()
    .slice(0, MAX_LISTED_NAMES);

  return names.length > 0 ? names.join(",") : undefined;
}

function sanitizeConsoleBreadcrumb(crumb: Breadcrumb): void {
  const next: Record<string, unknown> = { ...crumb.data };
  const consoleArguments = Array.isArray(next.arguments)
    ? (scrubFreeform(next.arguments) as unknown[])
    : undefined;
  delete next.arguments;
  const scrubbed = scrubUnknown(next) as Record<string, unknown>;
  crumb.data = (
    consoleArguments ? { ...scrubbed, arguments: consoleArguments } : scrubbed
  ) as Breadcrumb["data"];
}

function scrubBreadcrumbData(crumb: Breadcrumb): void {
  if (
    crumb.category === "fetch" ||
    crumb.category === "http" ||
    crumb.category === "xhr"
  ) {
    sanitizeNetworkBreadcrumb(crumb);
  } else if (crumb.category === "console" && crumb.data) {
    sanitizeConsoleBreadcrumb(crumb);
  } else {
    crumb.data = scrubUnknown(crumb.data) as Breadcrumb["data"];
  }
}

function scrubBreadcrumb(crumb: Breadcrumb): Breadcrumb | null {
  if (crumb.category) {
    crumb.category = BREADCRUMB_CATEGORIES.has(crumb.category)
      ? crumb.category
      : REDACTED_VALUE;
  }

  if (crumb.type && !BREADCRUMB_TYPES.has(crumb.type)) {
    crumb.type = REDACTED_VALUE;
  }

  if (crumb.event_id && !EVENT_ID_RE.test(crumb.event_id)) {
    crumb.event_id = REDACTED_VALUE;
  }

  if (typeof crumb.message === "string") {
    crumb.message = scrubFreeformString(crumb.message);
  }

  scrubBreadcrumbData(crumb);

  return crumb;
}

function scrubLogEntry(event: Event): void {
  if (!event.logentry) {
    return;
  }

  if (typeof event.logentry.message === "string") {
    event.logentry.message = scrubFreeformString(event.logentry.message);
  }

  if (event.logentry.params) {
    event.logentry.params = event.logentry.params.map(() => REDACTED_VALUE);
  }
}

function isUnsafeThreadId(id: number | string | undefined): boolean {
  if (typeof id === "string") {
    return true;
  }

  return typeof id === "number" && !isSafeFrameNumber(id);
}

function scrubMechanism(mechanism: Mechanism): void {
  if (mechanism.data) {
    mechanism.data = scrubUnknown(mechanism.data) as NonNullable<Mechanism["data"]>;
  }

  if (mechanism.type && !MECHANISM_RE.test(mechanism.type)) {
    mechanism.type = REDACTED_VALUE;
  }

  if (mechanism.source && !MECHANISM_RE.test(mechanism.source)) {
    mechanism.source = REDACTED_VALUE;
  }
}

function scrubException(exception: Exception): void {
  if (exception.type) {
    exception.type = ERROR_TYPE_IDENTIFIER_RE.test(exception.type)
      ? exception.type
      : REDACTED_VALUE;
  }

  if (typeof exception.value === "string") {
    exception.value = SAFE_ERROR_MESSAGES.has(exception.value)
      ? exception.value
      : scrubFreeformString(exception.value);
  }

  if (exception.module) {
    exception.module = REDACTED_VALUE;
  }

  if (isUnsafeThreadId(exception.thread_id)) {
    exception.thread_id = REDACTED_VALUE;
  }

  if (exception.mechanism) {
    scrubMechanism(exception.mechanism);
  }

  for (const frame of exception.stacktrace?.frames ?? []) {
    sanitizeStackFrame(frame);
  }
}

function scrubThread(thread: Thread): void {
  if (thread.name) {
    thread.name = REDACTED_VALUE;
  }

  if (isUnsafeThreadId(thread.id)) {
    thread.id = REDACTED_VALUE;
  }

  for (const frame of thread.stacktrace?.frames ?? []) {
    sanitizeStackFrame(frame);
  }
}

// Labels Sentry renders verbatim; only code-shaped loggers and known platforms
// survive.
function scrubEventLabels(event: Event): void {
  if (event.transaction) {
    event.transaction = REDACTED_VALUE;
  }

  if (
    event.fingerprint &&
    !event.fingerprint.every((key) => SAFE_FINGERPRINT_KEYS.has(key))
  ) {
    event.fingerprint = event.fingerprint.map(() => REDACTED_VALUE);
  }

  if (event.logger && !LOGGER_RE.test(event.logger)) {
    event.logger = REDACTED_VALUE;
  }

  if (event.server_name) {
    event.server_name = REDACTED_VALUE;
  }

  if (event.platform && !SAFE_PLATFORMS.has(event.platform)) {
    event.platform = REDACTED_VALUE;
  }
}

function scrubEventBreadcrumbs(event: Event): void {
  if (!event.breadcrumbs) {
    return;
  }

  // Scrub a copy: when the error dump is configured the scope keeps raw
  // breadcrumbs, and the next event's dump must still see them raw.
  event.breadcrumbs = event.breadcrumbs.flatMap((crumb) => {
    const scrubbed = beforeBreadcrumb({ ...crumb });

    return scrubbed ? [scrubbed] : [];
  });
}

// Structured surfaces stay on the default-deny scrubUnknown path.
function scrubEventContainers(event: Event): void {
  if (event.extra) {
    event.extra = scrubUnknown(event.extra) as Event["extra"];
  }

  if (event.contexts) {
    event.contexts = scrubUnknown(event.contexts) as Event["contexts"];
  }

  if (event.tags) {
    event.tags = scrubUnknown(event.tags) as Event["tags"];
  }
}

function scrubEventUser(event: Event): void {
  if (!event.user) {
    return;
  }

  const id =
    typeof event.user.id === "string" && OPAQUE_USER_ID_RE.test(event.user.id)
      ? event.user.id
      : undefined;
  event.user = id ? { id } : {};
}

function scrubEvent(event: Event): Event | null {
  if (event.event_id && !EVENT_ID_RE.test(event.event_id)) {
    return null;
  }

  if (typeof event.message === "string") {
    event.message = scrubFreeformString(event.message);
  }

  scrubLogEntry(event);

  for (const exception of event.exception?.values ?? []) {
    scrubException(exception);
  }

  for (const thread of event.threads?.values ?? []) {
    scrubThread(thread);
  }

  scrubEventLabels(event);
  sanitizeRequest(event);
  scrubEventBreadcrumbs(event);
  scrubEventContainers(event);
  scrubEventUser(event);

  return event;
}

function scrubSpan(span: SpanJSON, shouldHashDescription = true): SpanJSON {
  const scrubbedDescription = SCRUBBED_SPAN_DESCRIPTIONS.get(span);

  if (
    shouldHashDescription &&
    span.description &&
    span.description !== scrubbedDescription
  ) {
    span.description = hashPathLikeLabel(span.description);
  }

  if (shouldHashDescription && span.description) {
    SCRUBBED_SPAN_DESCRIPTIONS.set(span, span.description);
  }

  if (span.op) {
    span.op = SAFE_SPAN_OPERATIONS.has(span.op) ? span.op : REDACTED_VALUE;
  }

  delete span.origin;

  if (span.status) {
    span.status = SAFE_SPAN_STATUSES.has(span.status) ? span.status : REDACTED_VALUE;
  }

  span.data = scrubUnknown(span.data) as SpanJSON["data"];
  delete span.links;
  delete span.measurements;
  delete span.profile_id;

  return span;
}

const beforeSendLog = (log: Log): Log | null => {
  try {
    const message = typeof log.message === "string" ? log.message : String(log.message);
    const event = log.attributes?.event;

    if (typeof event !== "string" || !SAFE_EVENTS.has(event)) {
      return null;
    }

    if (message !== event && shouldRedactKey(message)) {
      return null;
    }

    log.message = event;

    if (event === "worker.analysis_usage_gap") {
      const reason = log.attributes?.reason;
      // This event is counts-only even if a caller supplies otherwise-safe IDs.
      log.attributes = {
        event,
        ...(log.attributes?.gap_count === 1 ? { gap_count: 1 } : {}),
        ...(new Set(["missing_counts", "invalid_counts", "unknown_model"]).has(
          reason as string
        )
          ? { reason }
          : {}),
      };

      return log;
    }

    const reason = log.attributes?.reason;
    log.attributes = scrubUnknown(log.attributes) as Log["attributes"];
    const reasonVocabulary = SAFE_LOG_EVENT_REASON_VOCABULARIES.get(event);

    if (reasonVocabulary && log.attributes && typeof reason === "string") {
      const scrubbedReason = scrubString(reason);
      log.attributes.reason = reasonVocabulary.has(scrubbedReason)
        ? scrubbedReason
        : REDACTED_VALUE;
    }

    return log;
  } catch {
    return null;
  }
};

const beforeSendSpan = (span: SpanJSON): SpanJSON => {
  try {
    const isTransactionRoot = span.is_segment === true && !span.segment_id;

    return scrubSpan(span, !isTransactionRoot);
  } catch {
    const fallback = {
      data: {},
      description: REDACTED_TRACE_LABEL,
      span_id: span.span_id,
      start_timestamp: span.start_timestamp,
      trace_id: span.trace_id,
    };

    return fallback;
  }
};

const beforeSendTransaction = (
  event: TransactionEvent,
  hint: EventHint
): TransactionEvent | null => {
  try {
    const transaction = event.transaction
      ? hashPathLikeLabel(event.transaction)
      : REDACTED_TRACE_LABEL;
    hint.attachments = [];
    const scrubbed = scrubEvent(event) as TransactionEvent | null;

    if (!scrubbed) {
      return null;
    }

    scrubbed.transaction = transaction;
    scrubbed.spans = scrubbed.spans?.map((span) => scrubSpan(span));
    delete scrubbed.measurements;
    delete scrubbed.sdkProcessingMetadata;
    delete scrubbed.transaction_info;

    return scrubbed;
  } catch {
    return null;
  }
};

const beforeSend = (event: ErrorEvent, hint: EventHint): ErrorEvent | null => {
  try {
    hint.attachments = [];

    return scrubEvent(event) as ErrorEvent | null;
  } catch {
    return null;
  }
};

const beforeBreadcrumb = (crumb: Breadcrumb): Breadcrumb | null => {
  try {
    return scrubBreadcrumb(crumb);
  } catch {
    return null;
  }
};

const BLOCKED_INTEGRATIONS = new Set([
  "BrowserSession",
  "LocalVariablesAsync",
  "ProcessSession",
]);

function filterIntegrations(integrations: Integration[]): Integration[] {
  return integrations.filter(
    (integration) => !BLOCKED_INTEGRATIONS.has(integration.name)
  );
}

export const phiSafeSentryOptions = {
  beforeBreadcrumb,
  beforeSend,
  beforeSendLog,
  beforeSendSpan,
  beforeSendTransaction,
  dataCollection: {
    cookies: false,
    frameContextLines: 5,
    genAI: { inputs: false, outputs: false },
    httpBodies: [],
    httpHeaders: { request: false, response: false },
    queryParams: false,
    stackFrameVariables: false,
    userInfo: false,
  },
  enableLogs: false,
  enableMetrics: false,
  integrations: filterIntegrations,
  sendDefaultPii: false,
  streamGenAiSpans: false,
  traceLifecycle: "static",
} satisfies Pick<
  Options,
  | "beforeBreadcrumb"
  | "beforeSend"
  | "beforeSendLog"
  | "beforeSendSpan"
  | "beforeSendTransaction"
  | "dataCollection"
  | "enableLogs"
  | "enableMetrics"
  | "integrations"
  | "sendDefaultPii"
  | "streamGenAiSpans"
  | "traceLifecycle"
>;

type PhiSafeRuntimeOptions = Omit<
  Options,
  | "beforeBreadcrumb"
  | "beforeSend"
  | "beforeSendLog"
  | "beforeSendMetric"
  | "beforeSendSpan"
  | "beforeSendTransaction"
  | "dataCollection"
  | "enableLogs"
  | "enableMetrics"
  | "integrations"
  | "sendDefaultPii"
  | "streamGenAiSpans"
  | "traceLifecycle"
> & {
  enableLogs?: boolean;
  integrations?: Integration[];
  beforeScrubErrorEvent?: BeforeScrubErrorEvent;
};

export type BeforeScrubErrorEvent = (
  event: ErrorEvent,
  hint: EventHint
) => void | PromiseLike<void>;

// The dump needs the pre-scrub event, but dump failures must never block
// Sentry from scrubbing and sending that same event.
function composeBeforeSend(
  beforeScrubErrorEvent: BeforeScrubErrorEvent
): (event: ErrorEvent, hint: EventHint) => Promise<ErrorEvent | null> {
  return async (event, hint) => {
    if (event.extra) {
      delete event.extra.context_url;
    }

    let callbackCompleted = false;

    try {
      await beforeScrubErrorEvent(event, hint);
      callbackCompleted = true;
    } catch {
      // The scrubbed Sentry event remains the fallback diagnostic.
    }

    const contextUrl = callbackCompleted ? event.extra?.context_url : undefined;

    if (event.extra) {
      delete event.extra.context_url;
    }

    const scrubbed = beforeSend(event, hint);

    if (
      scrubbed &&
      typeof contextUrl === "string" &&
      isApprovedErrorDumpUrl(contextUrl)
    ) {
      scrubbed.extra = {
        ...(scrubbed.extra ?? {}),
        context_url: contextUrl,
      };
    }

    return scrubbed;
  };
}

// With the error dump configured, breadcrumbs stay raw in process memory until
// an event is sent: the dump (BAA-covered, KMS-encrypted) gets them whole and
// beforeSend/beforeSendTransaction scrub them on the way to Sentry. Without a
// dump there is no PHI-capable sink, so they are scrubbed when recorded.
const keepBreadcrumbForSendTimeScrub = (crumb: Breadcrumb): Breadcrumb => crumb;

/**
 * Runs one unit of work (a queue message, a scheduled job) in its own Sentry
 * isolation scope that starts with no breadcrumbs, so an error's breadcrumbs
 * and tags describe that message alone.
 */
export function runInIsolatedSentryScope<T>(work: () => Promise<T>): Promise<T> {
  return withIsolationScope((scope) => {
    scope.clearBreadcrumbs();

    return work();
  });
}

export function createPhiSafeSentryOptions(
  runtimeOptions: PhiSafeRuntimeOptions
): Options {
  const {
    enableLogs = false,
    integrations: runtimeIntegrations = [],
    beforeScrubErrorEvent,
    ...options
  } = runtimeOptions;

  return {
    ...options,
    ...phiSafeSentryOptions,
    beforeBreadcrumb: beforeScrubErrorEvent
      ? keepBreadcrumbForSendTimeScrub
      : beforeBreadcrumb,
    beforeSend: beforeScrubErrorEvent
      ? composeBeforeSend(beforeScrubErrorEvent)
      : beforeSend,
    enableLogs,
    integrations:
      runtimeIntegrations.length === 0
        ? filterIntegrations
        : (defaultIntegrations) =>
            filterIntegrations([...defaultIntegrations, ...runtimeIntegrations]),
  };
}
